/* eslint-disable @typescript-eslint/consistent-type-imports, @typescript-eslint/no-unused-vars, @typescript-eslint/array-type, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-non-null-assertion, prefer-const, @typescript-eslint/explicit-function-return-type, @typescript-eslint/restrict-template-expressions, no-labels, @typescript-eslint/no-confusing-void-expression, @typescript-eslint/prefer-optional-chain, @typescript-eslint/no-unnecessary-boolean-literal-compare, no-empty, @typescript-eslint/prefer-nullish-coalescing, no-lone-blocks, @typescript-eslint/no-dynamic-delete, no-unmodified-loop-condition, @typescript-eslint/naming-convention, camelcase, quotes, @typescript-eslint/restrict-plus-operands */
/* gridRotations no long in use for plots and replicants */

import * as turf from "@turf/turf";
import {
  getFieldPolygons,
  getSubPolygons,
} from "../../../shared/utils/geoUtils";
import { createNutrientPlotColorScale } from "../../../shared/utils/nutrientPlotColors";
import mapboxgl from "mapbox-gl";
import moveIcon from "../../../assets/images/moveIcon.svg";
import rotateImage from "../../../assets/images/rotation.svg";
import {
  autozoneRequestStatus,
  ProductivityZonesInterface,
} from "../../../pages/autozones/types";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import {
  type AbLine,
  type NewTrialStateInterface,
  type PlotsValidationData,
  type RatesDosageDataInterface,
  type Replicants,
  type TrialPlot,
} from "../../../pages/new-trail/types";
import { allowFieldInputChange } from "../../../pages/new-trail/utils";
import {
  updateTrialPlotsWithStripGeometries,
  usePlotCardState,
} from "../../../shared/layout/PlotCard/utils";
import {
  getCircleColor,
  getCircleColorBiologicals,
  getCircleColorNutrient,
} from "../../../shared/layout/Plots/utils";
import { drawMachineryGridPure } from "../../utils/abLine";
import { addAutoZonesLayer } from "../../utils/autozones";
import { addBufferZoneLayer } from "../../utils/bufferZone";
import { addFieldOperationsLayer } from "../../utils/fieldOperations";
import { addABLinesToMap } from "../../utils/line";
import { isMapToolCapturingClicks } from "../../utils/mapToolInteraction";
import { addSoilImageLayer, removeSoilImageLayer } from "../../utils/soilData";
import {
  addStripLinesToMap,
  removeStripLinesFromMap,
} from "../../utils/stripLineJitendra";
import {
  calculateStripAreas,
  generateParallelLines,
} from "../../utils/parallelLinesLayer";
import {
  type FieldDataInterface,
  type GridFeature,
  type GridPosition,
  type PolygonGeoJSON,
  type TrialInfoInterface,
} from "./type";

const FEATURES_INDEX_ZERO = 0;
const randomPointsCache: Record<
  number,
  turf.FeatureCollection<turf.Point>
> = {};
let previousTrialPlots: TrialPlot[] | null = null;
const RGBA_TRIAL_PLOT = "white";
const RGBA_TRIAL_PLOT_RED = "rgba(255,0,0,1)";
export const markersForTrialPlot: Record<string, mapboxgl.Marker> = {};
const INDEX_ZERO = 0;
const INDEX_ONE = 1;
const RGBA_DASH_LINE = "rgba(49, 180, 242, 1)";
const WHITE_COLOR = "white";
type GeoJSON = turf.FeatureCollection<turf.Geometry>;
const gridPositions: Record<number, GridPosition> = {};
const gridRotations: Record<number, number> = {};
const RGBA_TRANSPARENT_COLOR = "rgba(0,0,0,0)";
const RGBA_PLOT_FILL = "rgba(255, 255, 255, 0.10)";

// --- AB line alignment tracking ---
type AlignmentChangeCallback = (misaligned: boolean) => void;
let alignmentChangeCallback: AlignmentChangeCallback | null = null;
let moduleMap: mapboxgl.Map | null = null;
/** Live reference to the latest newTrialState – mutated in-place by drag handlers */
let moduleNewTrialState: NewTrialStateInterface | null = null;

/**
 * Reads the index of the AB line marked `selectedForAlignment` from state.
 * Falls back to 0 so the first AB line is always the default target.
 */
const getAlignmentAbLineIndex = (state: NewTrialStateInterface): number => {
  const idx = state.ab_line?.findIndex((ab: AbLine) => ab.selectedForAlignment);
  return idx !== undefined && idx >= 0 ? idx : 0;
};

/** Register a callback that fires whenever plot alignment with the AB line changes. */
export const registerAlignmentChangeCallback = (
  cb: AlignmentChangeCallback | null
): void => {
  alignmentChangeCallback = cb;
};

/** @deprecated Selection is now driven by `selectedForAlignment` on state – this is a no-op kept for compile compatibility. */
export const setAlignmentAbLineIndex = (_idx: number): void => {};

/**
 * Helper: minimum angular difference (0–90) between two *undirected* bearings.
 * Treats a bearing and its 180° reverse as identical (same line, opposite direction)
 * so an AB line stored in either coordinate order is handled correctly.
 */
const bearingDiff = (a: number, b: number): number => {
  const diff = Math.abs(((a - b + 540) % 360) - 180);
  return Math.min(diff, 180 - diff);
};

const ALIGNMENT_TOLERANCE_DEG = 2;
// Distance a plot border may sit from a machinery guide line and still count as
// "aligned". The machinery grid offsets are re-measured from the rendered map
// source, whose geometry is tile-quantised (sub-metre) and follows a sampled
// rhumb path (documented residual up to ~0.21 m at 20 km from the A/B origin).
// A 0.25 m tolerance left almost no headroom, so a plot the user had just
// snapped onto a line was intermittently re-flagged as misaligned. 1 m is still
// far tighter than half a grid spacing (typically ≥ 10 m), so genuinely
// off-grid plots are still detected.
const COLLINEAR_DISTANCE_TOLERANCE_METERS = 1.0;

const toLineFeatures = (features: any[]): turf.Feature<turf.LineString>[] =>
  features.filter(
    (feature): feature is turf.Feature<turf.LineString> =>
      feature?.geometry?.type === "LineString"
  );

/** 1) Preferred: query currently available source features through Map API. */
const queryLiveSourceLines = (
  map: mapboxgl.Map,
  sourceId: string
): turf.Feature<turf.LineString>[] => {
  try {
    const queried = map.querySourceFeatures(sourceId as any) as any[];
    if (Array.isArray(queried) && queried.length > 0) {
      return toLineFeatures(queried);
    }
  } catch {
    // Source may not be queryable yet (not loaded / missing tiles) – continue fallback chain.
  }
  return [];
};

/** 2) Semi-public: source.getData() (available in some mapbox/maplibre builds). */
const getSourceDataLines = (source: any): turf.Feature<turf.LineString>[] => {
  try {
    const maybeData = source?.getData?.();
    if (maybeData && typeof maybeData.then !== "function") {
      const maybeFeatures = Array.isArray(maybeData?.features)
        ? maybeData.features
        : [];
      return toLineFeatures(maybeFeatures);
    }
  } catch {
    // Keep falling back.
  }
  return [];
};

/** 3) Style-source definition often carries inline GeoJSON `data` for source type geojson. */
const getStyleSourceLines = (
  map: mapboxgl.Map,
  sourceId: string
): turf.Feature<turf.LineString>[] => {
  const styleSourceData = (map.getStyle()?.sources as any)?.[sourceId]?.data;
  if (styleSourceData && Array.isArray(styleSourceData?.features)) {
    return toLineFeatures(styleSourceData.features);
  }
  return [];
};

/** 4) Last resort: private field used historically by GeoJSONSource. */
const getPrivateSourceLines = (
  source: any
): turf.Feature<turf.LineString>[] => {
  const privateData = source?._data;
  const privateFeatures = Array.isArray(privateData?.features)
    ? privateData.features
    : [];
  return toLineFeatures(privateFeatures);
};

export const getSourceLineFeatures = (
  map: mapboxgl.Map,
  sourceId: string
): turf.Feature<turf.LineString>[] => {
  const liveLines = queryLiveSourceLines(map, sourceId);
  if (liveLines.length > 0) return liveLines;

  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;

  const dataLines = getSourceDataLines(source);
  if (dataLines.length > 0) return dataLines;

  const styleLines = getStyleSourceLines(map, sourceId);
  if (styleLines.length > 0) return styleLines;

  return getPrivateSourceLines(source);
};

const getAlignmentGuideLines = (
  state: NewTrialStateInterface,
  map: mapboxgl.Map
): turf.Feature<turf.LineString>[] => {
  const alignmentAb = state.ab_line?.[getAlignmentAbLineIndex(state)];
  const abCoords = alignmentAb?.geojson?.geometry?.coordinates;
  if (!abCoords || abCoords.length < 2) return [];

  const abLineFeature = turf.lineString([
    abCoords[0] as [number, number],
    abCoords[1] as [number, number],
  ]);

  const gridSourceId = `grid-layer-${alignmentAb?.id ?? ""}`;
  const regularGridLines = getSourceLineFeatures(map, gridSourceId);
  const middleGridLines = getSourceLineFeatures(map, `${gridSourceId}-gray`);

  return [abLineFeature, ...regularGridLines, ...middleGridLines];
};

const getSignedPerpendicularDistanceKm = (
  originCoord: [number, number],
  pointCoord: [number, number],
  perpendicularBearing: number
): number => {
  const origin = turf.point(originCoord);
  const point = turf.point(pointCoord);
  const distanceKm = turf.rhumbDistance(origin, point, { units: "kilometers" });
  const bearingToPoint = turf.rhumbBearing(origin, point);
  const angleDiff = ((bearingToPoint - perpendicularBearing + 540) % 360) - 180;
  return distanceKm * Math.cos((angleDiff * Math.PI) / 180);
};

const getPlotBorderDistanceBandKm = (
  featureCollection: turf.FeatureCollection<turf.Polygon>
): { min: number; max: number; points: [number, number][] } => {
  const points: [number, number][] = [];
  featureCollection.features.forEach((feature) => {
    const ring = feature?.geometry?.coordinates?.[0] ?? [];
    ring.forEach((coord) => {
      if (Array.isArray(coord) && coord.length >= 2) {
        points.push([coord[0], coord[1]]);
      }
    });
  });

  if (points.length === 0) {
    return {
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
      points,
    };
  }

  const lngValues = points.map((point) => point[0]);
  return {
    min: Math.min(...lngValues),
    max: Math.max(...lngValues),
    points,
  };
};

const getGuideLineOffsetsKm = (
  guideLines: turf.Feature<turf.LineString>[],
  originCoord: [number, number],
  perpendicularBearing: number
): number[] => {
  const offsets = guideLines
    .map((line) => {
      const coords = line?.geometry?.coordinates;
      if (!coords || coords.length === 0) return null;
      const sampleCoord = coords[Math.floor(coords.length / 2)] as [
        number,
        number
      ];
      return getSignedPerpendicularDistanceKm(
        originCoord,
        sampleCoord,
        perpendicularBearing
      );
    })
    .filter(
      (offset): offset is number =>
        typeof offset === "number" && Number.isFinite(offset)
    );

  return Array.from(new Set(offsets.map((value) => Number(value.toFixed(9)))));
};

const snapFeatureCollectionToGuides = (
  featureCollection: turf.FeatureCollection<turf.Polygon>,
  pivot: [number, number],
  abCoords: [number, number][],
  abBearing: number,
  perpendicularBearing: number,
  guideOffsetsKm: number[]
): turf.FeatureCollection<turf.Polygon> => {
  const firstRing = featureCollection.features[0]?.geometry?.coordinates?.[0];
  if (!firstRing || firstRing.length < 2) {
    return featureCollection;
  }

  const currentEdgeBearing = turf.bearing(
    turf.point(firstRing[0]),
    turf.point(firstRing[1])
  );

  const normalizeDelta = (deltaValue: number): number =>
    ((deltaValue + 540) % 360) - 180;
  const delta1 = normalizeDelta(abBearing - currentEdgeBearing);
  const delta2 = normalizeDelta(abBearing + 180 - currentEdgeBearing);
  const delta = Math.abs(delta1) <= Math.abs(delta2) ? delta1 : delta2;

  const snappedFC = turf.transformRotate(featureCollection as any, delta, {
    pivot,
  }) as turf.FeatureCollection<turf.Polygon>;

  if (guideOffsetsKm.length === 0) {
    return snappedFC;
  }

  const band = getPlotBorderDistanceBandKm(snappedFC);
  if (!Number.isFinite(band.min) || !Number.isFinite(band.max)) {
    return snappedFC;
  }

  let minOffset = Number.POSITIVE_INFINITY;
  let maxOffset = Number.NEGATIVE_INFINITY;

  band.points.forEach((coord) => {
    const offset = getSignedPerpendicularDistanceKm(
      abCoords[0] as [number, number],
      coord,
      perpendicularBearing
    );
    if (offset < minOffset) minOffset = offset;
    if (offset > maxOffset) maxOffset = offset;
  });

  const borderOffsets = [minOffset, maxOffset];
  let bestShiftKm = 0;
  let bestShiftAbs = Number.POSITIVE_INFINITY;

  borderOffsets.forEach((borderOffset) => {
    guideOffsetsKm.forEach((guideOffset) => {
      const shiftKm = guideOffset - borderOffset;
      const absShift = Math.abs(shiftKm);
      if (absShift < bestShiftAbs) {
        bestShiftAbs = absShift;
        bestShiftKm = shiftKm;
      }
    });
  });

  if (!Number.isFinite(bestShiftKm) || Math.abs(bestShiftKm) <= 1e-9) {
    return snappedFC;
  }

  return turf.transformTranslate(
    snappedFC as any,
    bestShiftKm,
    perpendicularBearing,
    {
      units: "kilometers",
    }
  ) as turf.FeatureCollection<turf.Polygon>;
};

interface SnapRenderablePlotOptions {
  sourceId: string;
  centerMarkerId: string;
  rotationMarkerId: string;
  rawCoords?: any[];
  setCoordinates: (coordinates: any[]) => void;
  onAfterSnap?: (translatedFC: turf.FeatureCollection<turf.Polygon>) => void;
}

const validateSnapTargetInputs = (
  target: SnapRenderablePlotOptions,
  source: mapboxgl.GeoJSONSource | undefined
): boolean => {
  const centerMarker = markersForTrialPlot[target.centerMarkerId];
  return (
    !!source &&
    !!centerMarker &&
    !!target.rawCoords &&
    target.rawCoords.length > 0
  );
};

const buildFeatureCollectionFromCoords = (
  rawCoords: any[]
): turf.FeatureCollection<turf.Polygon> | null => {
  try {
    const features = rawCoords.map((polyCoords: any) =>
      turf.polygon(polyCoords)
    );
    return turf.featureCollection(
      features
    ) as turf.FeatureCollection<turf.Polygon>;
  } catch (error) {
    console.warn(
      "snapPlotsToAbLine: could not reconstruct FeatureCollection",
      error
    );
    return null;
  }
};

const updateMarkersAfterSnap = (
  target: SnapRenderablePlotOptions,
  translatedFC: turf.FeatureCollection<turf.Polygon>
): void => {
  const centerMarker = markersForTrialPlot[target.centerMarkerId];
  const translatedCenter = turf.center(translatedFC).geometry.coordinates as [
    number,
    number
  ];
  centerMarker.setLngLat(translatedCenter);

  const rotMarker = markersForTrialPlot[target.rotationMarkerId];
  if (rotMarker && translatedFC.features.length > 0) {
    const nwCorner = translatedFC.features[0].geometry.coordinates[0][1];
    rotMarker.setLngLat(nwCorner as [number, number]);
  }
};

const applySnapToRenderablePlot = (
  map: mapboxgl.Map,
  target: SnapRenderablePlotOptions,
  abCoords: [number, number][],
  abBearing: number,
  perpendicularBearing: number,
  guideOffsetsKm: number[]
): void => {
  const source = map.getSource(target.sourceId) as
    | mapboxgl.GeoJSONSource
    | undefined;

  if (!validateSnapTargetInputs(target, source)) {
    return;
  }

  const currentFC = buildFeatureCollectionFromCoords(target.rawCoords!);
  if (!currentFC) {
    return;
  }

  try {
    const centerMarker = markersForTrialPlot[target.centerMarkerId];
    const pivot = centerMarker.getLngLat().toArray() as [number, number];
    const translatedFC = snapFeatureCollectionToGuides(
      currentFC,
      pivot,
      abCoords,
      abBearing,
      perpendicularBearing,
      guideOffsetsKm
    );

    source!.setData(translatedFC as any);
    target.setCoordinates(
      translatedFC.features.map((feature) => feature.geometry.coordinates)
    );

    updateMarkersAfterSnap(target, translatedFC);
    target.onAfterSnap?.(translatedFC);
  } catch (error) {
    console.warn("snapPlotsToAbLine: rotation error", error);
  }
};

const hasBorderCollinearWithGuides = (
  grid: turf.FeatureCollection<turf.Polygon>,
  guideOffsetsKm: number[],
  originCoord: [number, number],
  perpendicularBearing: number
): boolean => {
  if (guideOffsetsKm.length === 0) return false;

  const band = getPlotBorderDistanceBandKm(grid);
  if (!Number.isFinite(band.min) || !Number.isFinite(band.max)) return false;

  let minOffset = Number.POSITIVE_INFINITY;
  let maxOffset = Number.NEGATIVE_INFINITY;
  band.points.forEach((coord) => {
    const offset = getSignedPerpendicularDistanceKm(
      originCoord,
      coord,
      perpendicularBearing
    );
    if (offset < minOffset) minOffset = offset;
    if (offset > maxOffset) maxOffset = offset;
  });

  const toleranceKm = COLLINEAR_DISTANCE_TOLERANCE_METERS / 1000;
  return guideOffsetsKm.some(
    (guideOffset) =>
      Math.abs(guideOffset - minOffset) <= toleranceKm ||
      Math.abs(guideOffset - maxOffset) <= toleranceKm
  );
};

/**
 * Check all trial plots against the current AB line (reads live mutation-updated state)
 * and fire the alignment callback.
 * Works both after plot rotation and after AB line drag because both drag handlers
 * mutate coordinates directly on the objects that moduleNewTrialState holds references to.
 */
const checkAndReportAlignment = (): void => {
  if (!alignmentChangeCallback) return;
  if (!moduleNewTrialState) return;
  if (!moduleMap) return;

  const plots = moduleNewTrialState.trial_plots;
  // No plots yet – nothing to check
  if (!plots || plots.length === 0) return;

  const abLine =
    moduleNewTrialState.ab_line?.[getAlignmentAbLineIndex(moduleNewTrialState)];
  const abCoords = abLine?.geojson?.geometry?.coordinates;
  if (!abCoords || abCoords.length < 2) return;
  // Guard against partially-initialised coordinates (e.g. null/empty entries
  // that occur briefly when all AB lines are deleted and a new one is added)
  const [c0, c1] = abCoords;
  if (
    !Array.isArray(c0) ||
    c0.length < 2 ||
    !Array.isArray(c1) ||
    c1.length < 2
  )
    return;

  const abBearing = turf.bearing(turf.point(c0), turf.point(c1));
  const perpendicularBearing = (((abBearing + 90) % 360) + 360) % 360;
  const guideLines = getAlignmentGuideLines(moduleNewTrialState, moduleMap);
  const guideOffsetsKm = getGuideLineOffsetsKm(
    guideLines,
    c0 as [number, number],
    perpendicularBearing
  );

  const anyMisaligned = plots.some((trialItem) => {
    // Strip plots are aligned to strip lines, not the AB line – skip them
    if (trialItem?.plot?.isStripPlot) return false;
    // coordinates = array of per-cell polygon coordinate sets
    // each cell = [ [ [lng,lat], [lng,lat], ... ] ]  (turf polygon format)
    const coordSets = trialItem?.plot?.geojson?.geometry?.coordinates as any[];
    if (!coordSets || coordSets.length === 0) return false;
    try {
      const features = coordSets
        .map((polyCoords: any) => {
          try {
            return turf.polygon(polyCoords);
          } catch {
            return null;
          }
        })
        .filter((feature): feature is turf.Feature<turf.Polygon> => !!feature);
      if (features.length === 0) return false;

      const grid = turf.featureCollection(features);

      // Fallback to legacy angular check if guide lines are unavailable.
      if (guideOffsetsKm.length === 0) {
        const firstRing: number[][] = coordSets[0][0];
        if (!firstRing || firstRing.length < 2) return false;
        const edgeBearing = turf.bearing(
          turf.point(firstRing[0]),
          turf.point(firstRing[1])
        );
        return bearingDiff(edgeBearing, abBearing) > ALIGNMENT_TOLERANCE_DEG;
      }

      const isCollinear = hasBorderCollinearWithGuides(
        grid,
        guideOffsetsKm,
        c0 as [number, number],
        perpendicularBearing
      );

      return !isCollinear;
    } catch {
      return false;
    }
  });

  alignmentChangeCallback(anyMisaligned);
};

/**
 * Exported entry-point for external callers (e.g. AB-line drag handler in line.ts).
 * Checks whether any plot is misaligned with the AB line and fires the callback.
 */
export const checkAlignmentWithCurrentState = (): void => {
  checkAndReportAlignment();
};

/**
 * Snap all trial plot groups to the current AB line bearing.
 * Call this when the user clicks "Snap to A/B line".
 */
export const snapPlotsToAbLine = (
  newTrialState: NewTrialStateInterface,
  setNewTrialState: (updater: (prev: any) => any) => void
): void => {
  if (!moduleMap) return;
  const map = moduleMap;

  if (!newTrialState.ab_line || newTrialState.ab_line.length === 0) return;
  const firstAb = newTrialState.ab_line[getAlignmentAbLineIndex(newTrialState)];
  const abCoords = firstAb?.geojson?.geometry?.coordinates;
  if (!abCoords || abCoords.length < 2) return;

  const abBearing = turf.bearing(
    turf.point(abCoords[0]),
    turf.point(abCoords[1])
  );
  const perpendicularBearing = (((abBearing + 90) % 360) + 360) % 360;
  const guideLines = getAlignmentGuideLines(newTrialState, map);
  const guideOffsetsKm = getGuideLineOffsetsKm(
    guideLines,
    abCoords[0] as [number, number],
    perpendicularBearing
  );

  newTrialState.trial_plots?.forEach((trialItem: TrialPlot) => {
    // Strip plots are managed by the strip system – skip them
    if (trialItem?.plot?.isStripPlot) return;
    const trialPlotId = trialItem?.plot?.id as number;
    const gridSourceId = `grid-of-trial-plots-${trialPlotId}`;

    applySnapToRenderablePlot(
      map,
      {
        sourceId: gridSourceId,
        centerMarkerId: `envelope-center-marker-${trialPlotId}`,
        rotationMarkerId: `rotation-marker-${trialPlotId}`,
        rawCoords: trialItem?.plot?.geojson?.geometry?.coordinates,
        setCoordinates: (coordinates) => {
          trialItem.plot.geojson.geometry.coordinates = coordinates;
        },
        onAfterSnap: (translatedFC) => {
          updateGroupLabelPosition({
            map,
            baseSourceId: gridSourceId,
            rotatedGrid: translatedFC,
            newTrialState,
            trialPlotId,
            labelSuffix: "",
            offset: [0, -0.2],
            fontSize: 14,
          });
          updatePlotLabelMarkersPosition(translatedFC, trialPlotId);
        },
      },
      abCoords as [number, number][],
      abBearing,
      perpendicularBearing,
      guideOffsetsKm
    );

    trialItem.replicants?.forEach((replicant, cloneIndex) => {
      const replicantSourceId = `cloned-grid-of-trial-plots-${cloneIndex}-${trialPlotId}-${
        replicant.id as number
      }`;

      applySnapToRenderablePlot(
        map,
        {
          sourceId: replicantSourceId,
          centerMarkerId: `envelope-center-marker-replicant-${
            replicant.id as number
          }`,
          rotationMarkerId: `rotation-marker-replicant-${
            replicant.id as number
          }`,
          rawCoords: replicant?.geojson?.geometry?.coordinates,
          setCoordinates: (coordinates) => {
            replicant.geojson.geometry.coordinates = coordinates;
          },
          onAfterSnap: (translatedFC) => {
            updateGroupLabelPosition({
              map,
              baseSourceId: replicantSourceId,
              rotatedGrid: translatedFC,
              newTrialState,
              trialPlotId,
              labelSuffix: "'",
              offset: [0, 0],
              fontSize: 12,
            });
            updateReplicantPlotLabels(
              map,
              translatedFC,
              cloneIndex,
              trialPlotId
            );
          },
        },
        abCoords as [number, number][],
        abBearing,
        perpendicularBearing,
        guideOffsetsKm
      );
    });
  });

  // Trigger a React state refresh so downstream consumers see updated coordinates
  setNewTrialState((prev: any) => ({
    ...prev,
    trial_plots: prev.trial_plots.map((tp: TrialPlot) => ({
      ...tp,
      plot: { ...tp.plot },
      replicants: tp.replicants?.map((replicant: Replicants) => ({
        ...replicant,
      })),
    })),
  }));

  // Dismiss the banner
  if (alignmentChangeCallback) {
    alignmentChangeCallback(false);
  }
};

// Cache to prevent unnecessary strip line reprocessing
let stripLinesCache = new Set<string>();

// Track previous step to clear cache when step changes
let previousStep: number | undefined;

// Function to clear strip lines cache
export const clearStripLinesCache = () => {
  stripLinesCache.clear();
};

// Function to clear strip lines from both cache and map
export const clearStripLinesFromMap = (
  mapRef?: React.MutableRefObject<mapboxgl.Map | null>
) => {
  // Clear the cache first
  stripLinesCache.clear();

  // If mapRef is provided, use it directly
  if (mapRef?.current) {
    clearStripsFromMapInstance(mapRef.current, mapRef);
  } else {
    // Try to find mapbox instances in the DOM as fallback

    try {
      // Look for mapbox containers in the DOM
      const mapContainers = document.querySelectorAll(".mapboxgl-map");
      mapContainers.forEach((container) => {
        const mapInstance = (container as any)?._mapboxMap;
        if (mapInstance) {
          clearStripsFromMapInstance(mapInstance, { current: mapInstance });
        }
      });
    } catch (error) {
      console.warn("⚠️ Could not access map instances from DOM:", error);
    }
  }
};

// Helper function to clear strips from a specific map instance
const clearStripsFromMapInstance = (
  mapInstance: mapboxgl.Map,
  mapRef: React.MutableRefObject<mapboxgl.Map | null>
) => {
  try {
    // Remove strip lines with common source IDs using removeStripLinesFromMap function
    const commonSourceIds = ["strip-lines", "strip-src-0"];
    commonSourceIds.forEach((sourceId) => {
      try {
        removeStripLinesFromMap(mapRef, sourceId);
      } catch (error) {
        console.warn(
          `⚠️ Could not remove strip lines for source ${sourceId}:`,
          error
        );
      }
    });

    // Also remove any layers/sources with "strip" in the name as fallback
    const style = mapInstance.getStyle();
    if (style?.layers) {
      const stripLayers = style.layers.filter(
        (layer: any) =>
          layer.id.includes("strip") ||
          layer.id.includes("Strip") ||
          layer.id.includes("layer-strip") ||
          layer.source?.includes("strip")
      );

      stripLayers.forEach((layer: any) => {
        try {
          if (mapInstance.getLayer(layer.id)) {
            mapInstance.removeLayer(layer.id);
          }
        } catch (error) {
          console.warn(`⚠️ Could not remove layer ${layer.id}:`, error);
        }
      });
    }

    // Remove sources containing "strip"
    if (style?.sources) {
      const stripSources = Object.keys(style.sources).filter(
        (sourceId) => sourceId.includes("strip") || sourceId.includes("Strip")
      );

      stripSources.forEach((sourceId) => {
        try {
          if (mapInstance.getSource(sourceId)) {
            mapInstance.removeSource(sourceId);
          }
        } catch (error) {
          console.warn(`⚠️ Could not remove source ${sourceId}:`, error);
        }
      });
    }
  } catch (error) {
    console.error("❌ Error clearing strips from map instance:", error);
  }
};

const updateABWidth = (
  abItem: AbLine,
  sourceId: string,
  map: mapboxgl.Map,
  bboxOld: any,
  polygonGeoJSON: PolygonGeoJSON,
  trialProtocolData?: any
) => {
  if (abItem.grid_width === 0) return;
  // turf.feature (not turf.polygon) keeps MultiPolygon field geometries intact so
  // the machinery grid is clipped inside every sub-polygon.
  const turfPolygon = turf.feature(
    polygonGeoJSON.features[0].geometry
  ) as turf.Feature<turf.Polygon | turf.MultiPolygon>;

  const layerId = `grid-line-layer-${abItem.id ?? ""}`;
  const sourceIdGrid = `grid-layer-${abItem.id ?? ""}`;

  drawMachineryGridPure(
    map,
    turfPolygon,
    abItem.geojson.geometry.coordinates[0],
    abItem.geojson.geometry.coordinates[1],
    sourceIdGrid,
    layerId,
    {
      spacing: abItem.grid_width,
      drawGreyLines: true,
      visible: abItem.visible ?? true,
    }
  );

  // Keep the machinery grid (main + grey) as the BOTTOM-most trial layer so
  // EVERYTHING — field, plot/cell rate colours, strip lines and labels — draws
  // on top of it and stays visible. updateABWidth is fired by a separate effect
  // that can run after those layers, so move the grid under the lowest of them.
  // "polygon-fill" (the field) is added at map load, so it is the lowest custom
  // layer; fall back to the first plot fill (`grid-of-trial-plots-*`) if absent.
  const anchorLayerId = map.getLayer("polygon-fill")
    ? "polygon-fill"
    : (map.getStyle()?.layers ?? []).find(
        (styleLayer) =>
          typeof (styleLayer as { source?: unknown }).source === "string" &&
          (styleLayer as { source: string }).source.startsWith(
            "grid-of-trial-plots-"
          )
      )?.id;
  if (anchorLayerId) {
    try {
      const grayLayerId = `${layerId}-gray`;
      // Main grid just below the anchor, grey just below the main grid.
      if (map.getLayer(layerId)) map.moveLayer(layerId, anchorLayerId);
      if (map.getLayer(grayLayerId)) map.moveLayer(grayLayerId, layerId);
    } catch {
      /* layer ordering is best-effort */
    }
  }
};

/**
 * Expand a plot's MultiPolygon coordinates into individual Polygon cell features.
 *
 * Why cell-level instead of MultiPolygon-level?
 * ─────────────────────────────────────────────
 * In Turf v6, `booleanContains(MultiPolygonA, B)` checks whether ANY SINGLE
 * polygon cell of A contains all of B.  When a plot is dragged inside another
 * plot but straddles several cells of that plot (the common case), no single
 * cell of A contains all of B, so the check wrongly returns false.
 *
 * Working at the individual cell (Polygon) level avoids this limitation.
 * Two plots overlap if and only if at least one cell of plot A shares area
 * with at least one cell of plot B.
 */
const getPlotCells = (plot: any): turf.Feature<turf.Polygon>[] => {
  try {
    const coords = plot?.geojson?.geometry?.coordinates;
    // coords[i] = polygon ring array for cell i: [[lng,lat],...]
    // A Point geometry stores coords as [lng, lat] — coords[0] is a number.
    if (!coords || !Array.isArray(coords) || coords.length === 0) return [];
    if (!Array.isArray(coords[0])) return []; // Point placeholder
    return coords
      .map((polygonCoords: any) => {
        try {
          return turf.polygon(polygonCoords);
        } catch {
          return null;
        }
      })
      .filter(Boolean) as turf.Feature<turf.Polygon>[];
  } catch {
    return [];
  }
};

/** True when any cell of A shares area with any cell of B. */
const cellSetsOverlap = (
  cellsA: turf.Feature<turf.Polygon>[],
  cellsB: turf.Feature<turf.Polygon>[]
): boolean =>
  cellsA.length > 0 &&
  cellsB.length > 0 &&
  cellsA.some((cellA) => cellsB.some((cellB) => cellsOverlap(cellA, cellB)));

/** Every plot/replicant pair (by id) whose cells share area. */
const findIntersectingPlotPairs = (
  allPlots: any[],
  plotCells: turf.Feature<turf.Polygon>[][]
): [number, number][] => {
  const pairs: [number, number][] = [];
  for (let i = 0; i < allPlots.length; i++) {
    for (let j = i + 1; j < allPlots.length; j++) {
      if (cellSetsOverlap(plotCells[i], plotCells[j])) {
        pairs.push([allPlots[i].id, allPlots[j].id]);
      }
    }
  }
  return pairs;
};

const checkPlotIntersections = (
  trialState: { trial_plots: TrialPlot[] },
  setValidationData: React.Dispatch<React.SetStateAction<PlotsValidationData>>
) => {
  if (!trialState?.trial_plots || !setValidationData) return;

  const allPlots: any = [];

  // Flatten all plots and replicants into a single array
  trialState.trial_plots.forEach((trialPlot) => {
    allPlots.push(trialPlot.plot);
    if (trialPlot.replicants) {
      allPlots.push(...trialPlot.replicants);
    }
  });

  // Pre-compute individual cell polygons for every plot/replicant
  const plotCells: turf.Feature<turf.Polygon>[][] = allPlots.map(getPlotCells);

  // Check every plot pair — two plots overlap when ANY cell pair shares area
  const newIntersectingPairs = findIntersectingPlotPairs(allPlots, plotCells);

  setValidationData((prevState: PlotsValidationData) => {
    if (
      JSON.stringify(prevState.intersectingPairs) !==
      JSON.stringify(newIntersectingPairs)
    ) {
      return {
        ...prevState,
        intersectingPairs: newIntersectingPairs,
      };
    }
    return prevState;
  });
};

const checkPlotBoundaries = (
  geoJsonData: any,
  plotsValidationData?: PlotsValidationData,
  setPlotsValidationData?: any,
  newTrialState?: any
) => {
  // ─── Plot-to-plot intersection check ────────────────────────────────────────
  // This MUST run unconditionally – it does not depend on field boundary geometry.
  // Moving it here ensures it is never skipped by early-returns below that are
  // only relevant for the field-boundary check.
  // Always run intersection check — independent of field geometry
  checkPlotIntersections(newTrialState, setPlotsValidationData);

  // ─── Field-boundary check ────────────────────────────────────────────────────
  // The remaining logic requires a valid simple-Polygon field boundary.
  // Early returns below only skip the field-boundary part, NOT the intersection check above.
  // Expand the field into its sub-polygons. A plain Polygon yields one entry; a
  // MultiPolygon yields one per detached parcel. A plot/cell is "inside the field"
  // when it lies within ANY sub-polygon, so plots may be placed in either parcel.
  const fieldPolygons = getFieldPolygons(geoJsonData);
  if (fieldPolygons.length === 0) return;

  const checkPolygon = (plotData: any, plotId: any) => {
    const plotCoordinates = plotData?.geojson?.geometry?.coordinates;

    const allPolygonsWithin =
      plotCoordinates && Array.isArray(plotCoordinates)
        ? plotCoordinates.every((polygonCoords: turf.Position[][]) => {
            if (!polygonCoords || !Array.isArray(polygonCoords)) return false;

            // Validate that each coordinate ring has at least 4 points (minimum for a valid polygon)
            const hasValidRings = polygonCoords.every((ring) => {
              if (!Array.isArray(ring) || ring.length < 4) {
                console.warn(
                  "⚠️ Invalid polygon ring - needs at least 4 points:",
                  { ringLength: ring?.length, ring }
                );
                return false;
              }
              return true;
            });

            if (!hasValidRings) {
              console.warn(
                "⚠️ Skipping polygon bounds check due to invalid rings"
              );
              return true; // Skip validation for invalid polygons to avoid crashes
            }

            try {
              const plotPolygon = turf.polygon(polygonCoords);
              // Valid when the cell is fully within at least one sub-polygon.
              return fieldPolygons.some((fieldPoly) =>
                turf.booleanWithin(plotPolygon, fieldPoly)
              );
            } catch (error) {
              console.warn("Error checking polygon bounds:", error);
              return false;
            }
          })
        : false;

    const exists =
      plotsValidationData?.polygonsNotInsideField?.includes(plotId);

    if (allPolygonsWithin) {
      if (exists) {
        setPlotsValidationData((prevState: any) => {
          const updatedPolygons = prevState.polygonsNotInsideField?.filter(
            (item: any) => item !== plotId
          );
          if (
            JSON.stringify(updatedPolygons) !==
            JSON.stringify(prevState.polygonsNotInsideField)
          ) {
            return {
              ...prevState,
              polygonsNotInsideField: updatedPolygons,
            };
          }
          return prevState;
        });
      }
    } else if (!exists) {
      setPlotsValidationData((prevState: any) => ({
        ...prevState,
        polygonsNotInsideField: [
          ...(prevState.polygonsNotInsideField || []),
          plotId,
        ],
      }));
    }
  };

  newTrialState?.trial_plots?.forEach((trialItem: TrialPlot) => {
    // Check main plot
    checkPolygon(trialItem.plot, trialItem.plot.id);

    // Check replicants
    trialItem.replicants?.forEach((replicant) => {
      checkPolygon(replicant, replicant.id);
    });
  });
};

/**
 * Snaps the leading edge of a rotated plot grid to the nearest machinery grid
 * line that runs parallel to the AB line.
 *
 * Grid lines are spaced `gridWidthMeters` metres apart along the axis that is
 * perpendicular to the AB line bearing (`abBearing + 90°`), measured from
 * `abOriginCoord`.  The function finds the minimum perpendicular projection of
 * any grid vertex, rounds it to the nearest multiple of the grid spacing, then
 * translates the entire feature-collection by the difference.
 *
 * @param gridGeoJSON       Rotated FeatureCollection of plot cell polygons.
 * @param abOriginCoord     First coordinate of the AB line [lng, lat].
 * @param abBearing         Bearing of the AB line in degrees.
 * @param gridWidthMeters   Machinery grid spacing in metres (i.e. `grid_width`).
 * @returns                 The (possibly translated) FeatureCollection, or the
 *                          original collection unchanged if inputs are invalid.
 */
export const snapGridToAbLine = (
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>,
  abOriginCoord: [number, number],
  abBearing: number,
  gridWidthMeters: number,
  options?: { mode?: "nearestGrid" | "abOnly" }
): turf.FeatureCollection<turf.Polygon> => {
  if (!gridGeoJSON || gridGeoJSON.features.length === 0) return gridGeoJSON;
  if (
    !abOriginCoord ||
    !Number.isFinite(abBearing) ||
    !Number.isFinite(gridWidthMeters) ||
    gridWidthMeters <= 0
  )
    return gridGeoJSON;

  const perpBearing = (((abBearing + 90) % 360) + 360) % 360;
  const gridSpacingKm = gridWidthMeters / 1000;
  const abOrigin = turf.point(abOriginCoord);

  let minPerpDist = Infinity;
  for (const feature of gridGeoJSON.features) {
    const ring = feature.geometry.coordinates[0];
    for (const coord of ring) {
      const pt = turf.point(coord as [number, number]);
      const dist = turf.rhumbDistance(abOrigin, pt, { units: "kilometers" });
      const bearingToPt = turf.rhumbBearing(abOrigin, pt);
      const angleDiff = ((bearingToPt - perpBearing + 540) % 360) - 180;
      const perpComp = dist * Math.cos((angleDiff * Math.PI) / 180);
      if (perpComp < minPerpDist) minPerpDist = perpComp;
    }
  }

  if (!Number.isFinite(minPerpDist)) return gridGeoJSON;

  const snappedDist =
    options?.mode === "abOnly"
      ? 0
      : Math.round(minPerpDist / gridSpacingKm) * gridSpacingKm;
  const shiftKm = snappedDist - minPerpDist;

  if (Math.abs(shiftKm) <= 1e-9) return gridGeoJSON;

  return turf.transformTranslate(gridGeoJSON, shiftKm, perpBearing, {
    units: "kilometers",
  }) as turf.FeatureCollection<turf.Polygon>;
};

const getRhumbOffsetPoint = (
  originCoord: [number, number],
  eastMeters: number,
  northMeters: number
): [number, number] => {
  let point = turf.point(originCoord);

  if (eastMeters !== 0) {
    point = turf.rhumbDestination(
      point,
      Math.abs(eastMeters) / 1000,
      eastMeters >= 0 ? 90 : 270,
      { units: "kilometers" }
    );
  }

  if (northMeters !== 0) {
    point = turf.rhumbDestination(
      point,
      Math.abs(northMeters) / 1000,
      northMeters >= 0 ? 0 : 180,
      { units: "kilometers" }
    );
  }

  return point.geometry.coordinates as [number, number];
};

export const buildAxisAlignedPlotGrid = (
  startingPoint: [number, number],
  rows: number,
  columns: number,
  plotWidthMeters: number,
  plotHeightMeters: number
): turf.FeatureCollection<turf.Polygon> => {
  const features: turf.Feature<turf.Polygon>[] = [];

  for (let rowIndex = INDEX_ZERO; rowIndex < rows; rowIndex++) {
    const southOffsetMeters = -rowIndex * plotHeightMeters;
    const northOffsetMeters = southOffsetMeters + plotHeightMeters;

    for (let columnIndex = INDEX_ZERO; columnIndex < columns; columnIndex++) {
      const westOffsetMeters = columnIndex * plotWidthMeters;
      const eastOffsetMeters = westOffsetMeters + plotWidthMeters;

      const southWest = getRhumbOffsetPoint(
        startingPoint,
        westOffsetMeters,
        southOffsetMeters
      );
      const northWest = getRhumbOffsetPoint(
        startingPoint,
        westOffsetMeters,
        northOffsetMeters
      );
      const northEast = getRhumbOffsetPoint(
        startingPoint,
        eastOffsetMeters,
        northOffsetMeters
      );
      const southEast = getRhumbOffsetPoint(
        startingPoint,
        eastOffsetMeters,
        southOffsetMeters
      );

      features.push(
        turf.polygon([[southWest, northWest, northEast, southEast, southWest]])
      );
    }
  }

  return turf.featureCollection(features);
};

/** True when a single plot cell lies completely inside at least one field parcel. */
const isFeatureWithinAnyPolygon = (
  feature: turf.Feature<turf.Polygon>,
  fieldPolygons: Array<turf.Feature<turf.Polygon>>
): boolean => {
  return fieldPolygons.some((fieldPoly) => {
    try {
      return turf.booleanWithin(feature, fieldPoly);
    } catch {
      return false;
    }
  });
};

/** Number of grid cells that lie completely inside the field (any parcel). */
const countCellsInsideField = (
  grid: turf.FeatureCollection<turf.Polygon>,
  fieldPolygons: Array<turf.Feature<turf.Polygon>>
): number => {
  return grid.features.reduce(
    (count, feature) =>
      isFeatureWithinAnyPolygon(feature, fieldPolygons) ? count + 1 : count,
    0
  );
};

/**
 * True when the WHOLE grid lies completely inside a SINGLE field parcel.
 * (A MultiPolygon field is a set of parcels; a plot block must sit inside one of
 * them, not straddle the gap between two.)
 */
const isGridInsideSingleParcel = (
  grid: turf.FeatureCollection<turf.Polygon>,
  fieldPolygons: Array<turf.Feature<turf.Polygon>>
): boolean => {
  if (grid.features.length === 0) return false;
  return fieldPolygons.some((fieldPoly) =>
    grid.features.every((cell) => {
      try {
        return turf.booleanWithin(cell, fieldPoly);
      } catch {
        return false;
      }
    })
  );
};

/** True when two plot cells share any interior area (partial / contained / within). */
const cellsOverlap = (
  a: turf.Feature<turf.Polygon>,
  b: turf.Feature<turf.Polygon>
): boolean => {
  try {
    return (
      turf.booleanOverlap(a, b) ||
      turf.booleanContains(a, b) ||
      turf.booleanWithin(a, b)
    );
  } catch {
    return false;
  }
};

/** True when any cell of the grid overlaps any already-placed plot footprint. */
const gridOverlapsFootprints = (
  grid: turf.FeatureCollection<turf.Polygon>,
  footprints: Array<turf.Feature<turf.Polygon>>
): boolean => {
  if (footprints.length === 0) return false;
  return grid.features.some((cell) =>
    footprints.some((footprint) => cellsOverlap(cell, footprint))
  );
};

/**
 * Collect the cell polygons of every OTHER non-strip plot (and its replicants)
 * so a newly-placed / edited plot can be positioned without landing on top of
 * an existing one. The plot currently being (re)generated is excluded by id.
 */
const collectExistingPlotFootprints = (
  state: NewTrialStateInterface,
  currentPlotId: number
): Array<turf.Feature<turf.Polygon>> => {
  const footprints: Array<turf.Feature<turf.Polygon>> = [];

  const addCoords = (coordsList: any): void => {
    if (!Array.isArray(coordsList) || !Array.isArray(coordsList[0])) return; // skip [0,0] point placeholders
    coordsList.forEach((polygonCoords: any) => {
      try {
        const poly = turf.polygon(polygonCoords);
        if (turf.area(poly) > 0) footprints.push(poly);
      } catch {
        // ignore malformed cell
      }
    });
  };

  state.trial_plots?.forEach((item: any) => {
    if (item?.plot?.isStripPlot) return;
    if (item?.plot?.id === currentPlotId) return;
    addCoords(item?.plot?.geojson?.geometry?.coordinates);
    item?.replicants?.forEach((replicant: any) =>
      addCoords(replicant?.geojson?.geometry?.coordinates)
    );
  });

  return footprints;
};

/**
 * Candidate centre points spread across a parcel, used to search for a free,
 * fully-inside placement for a new plot block. Random interior points give
 * spatial variety (so plots don't stack), and a guaranteed-interior anchor is
 * always appended as a last-resort candidate.
 */
const generatePlacementCandidatePoints = (
  placementArea: any,
  count: number
): Array<[number, number]> => {
  const points: Array<[number, number]> = [];

  try {
    const randomPoints = turf.randomPoint(count, {
      bbox: turf.bbox(placementArea),
    });
    const within = turf.pointsWithinPolygon(randomPoints, placementArea);
    within.features.forEach((feature: any) => {
      const coord = feature?.geometry?.coordinates;
      if (Array.isArray(coord) && coord.length >= 2) {
        points.push([coord[0], coord[1]]);
      }
    });
  } catch {
    // ignore – anchor fallback below still provides a candidate
  }

  try {
    const anchor = turf.pointOnFeature(placementArea).geometry.coordinates as [
      number,
      number
    ];
    points.push(anchor);
  } catch {
    // ignore
  }

  return points;
};

const updateStripsData = (
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  polygonGeoJSON: PolygonGeoJSON,
  setNewTrialState: any,
  currentStep?: number,
  setStripsData?: any,
  stripsData?: any
): void => {
  // setNewTrialState((prevState: NewTrialStateInterface) => {
  //   const updatedState = { ...prevState };
  //   if (currentStep !== 1) {
  //     updatedState.stripLines = newTrialState.stripLines?.map((strip,index) => ({
  //       ...strip,
  //       gridLineIndex: prevState.stripLines?.find((i) => i === strip)?.gridLineIndex? + 1 :
  //         index + 1
  //     }));
  //   }
  //   return updatedState;
  // });
  // const updatedData = [{ gridLineIndex: 0 }];
  // setStripsData(updatedData);
  // setNewTrialState((prevState: NewTrialStateInterface) => {
  //   const updatedState = { ...prevState, stripsData: updatedData };
  //   return updatedState;
  // });
};

/**
 * Map component utility hook that provides marker creation, map update orchestration,
 * AB line width updates, plot boundary validation, and strip line management helpers.
 *
 * @returns An API with functions to create custom marker elements, update map layers/data,
 * update AB line widths, validate plot boundaries, update strip data, and clear strip lines.
 */
const DEFAULT_STRIP_GRID_WIDTH = 100;

/** Marks trial plots as strip plots so they get recomputed by the strip engine. */
function markPlotsAsStripPlots(
  setNewTrialState: any,
  hasValidStripsMode: boolean
): void {
  setNewTrialState((prevState: any) => {
    const updatedTrialPlots = prevState.trial_plots.map((trialPlot: any) => {
      if (!trialPlot.plot.isStripPlot && hasValidStripsMode) {
        return {
          ...trialPlot,
          plot: {
            ...trialPlot.plot,
            isStripPlot: true,
            totalStrips: prevState.stripsData?.count || 2,
            stripIndices: Array.from(
              { length: prevState.stripsData?.count || 2 },
              (_, i) => i
            ),
            geometrySource: "manual", // Force recalculation
          },
        };
      }
      return trialPlot;
    });

    return {
      ...prevState,
      trial_plots: updatedTrialPlots,
    };
  });
}

/** Initial strip offsets: saved ones if present, else two defaults derived from AB line width. */
function resolveInitialStripOffsets(
  abLine: any,
  newTrialState: NewTrialStateInterface
): number[] {
  if (
    newTrialState.stripsData?.offsets &&
    newTrialState.stripsData.offsets.length > 0
  ) {
    return newTrialState.stripsData.offsets;
  }
  const selectedAbLineWidth =
    abLine.grid_width && abLine.grid_width > 0
      ? abLine.grid_width
      : DEFAULT_STRIP_GRID_WIDTH;
  return [selectedAbLineWidth, selectedAbLineWidth * 2];
}

/** Computes the new (sorted) strip offsets for one drag/snap adjustment, or null if not applicable. */
function computeAdjustedStripOffsets(
  data: {
    direction: "left" | "right";
    indexOfStripLine: number;
    action?: "step" | "snap";
    snappedDistance?: number;
  },
  newTrialState: NewTrialStateInterface
): number[] | null {
  const currentOffsets = newTrialState.stripsData?.offsets || [];
  const selectedAbLine =
    newTrialState.ab_line!.find((line) => line.selectedForStrips) ||
    newTrialState.ab_line![0];
  const gridWidth =
    selectedAbLine?.grid_width && selectedAbLine.grid_width > 0
      ? selectedAbLine.grid_width
      : DEFAULT_STRIP_GRID_WIDTH;
  const adjustmentStep = gridWidth / 2;

  const newOffsets = [...currentOffsets];

  // Resolve target index. For snap actions, map out-of-range indices
  // to the nearest existing offset so updates are not dropped.
  let targetIndex = Number.isInteger(data.indexOfStripLine)
    ? data.indexOfStripLine
    : -1;

  if (
    (targetIndex < 0 || targetIndex >= newOffsets.length) &&
    data.action === "snap" &&
    typeof data.snappedDistance === "number" &&
    Number.isFinite(data.snappedDistance) &&
    newOffsets.length > 0
  ) {
    let nearestOffsetIndex = 0;
    let nearestDelta = Number.POSITIVE_INFINITY;

    newOffsets.forEach((offset, index) => {
      const delta = Math.abs(offset - data.snappedDistance!);
      if (delta < nearestDelta) {
        nearestDelta = delta;
        nearestOffsetIndex = index;
      }
    });

    targetIndex = nearestOffsetIndex;
  }

  if (targetIndex < 0 || targetIndex >= newOffsets.length) {
    return null;
  }

  // Apply adjustment: either nudge by step or snap to an absolute line distance.
  if (
    data.action === "snap" &&
    typeof data.snappedDistance === "number" &&
    Number.isFinite(data.snappedDistance)
  ) {
    newOffsets[targetIndex] = data.snappedDistance;
  } else if (data.direction === "left") {
    newOffsets[targetIndex] -= adjustmentStep;
  } else {
    newOffsets[targetIndex] += adjustmentStep;
  }

  // Keep offsets sorted left→right. If a line is dragged past a neighbour the
  // order changes; re-sorting keeps the band boundaries and strip numbering
  // (1..N) consistent on the next recompute.
  return [...newOffsets].sort((a, b) => a - b);
}

/**
 * Async (debounced-by-setTimeout) strip-line drag/snap handler. Prevents
 * re-entrant adjustments while one is already scheduled.
 */
function createStripLineAdjustHandler(
  newTrialState: NewTrialStateInterface,
  setNewTrialState: any
): (data: {
  direction: "left" | "right";
  indexOfStripLine: number;
  action?: "step" | "snap";
  snappedDistance?: number;
}) => void {
  let isAdjusting = false; // Flag to prevent infinite recursion

  return (data) => {
    if (isAdjusting) {
      return;
    }
    isAdjusting = true;

    // Use setTimeout to break out of the current execution context and prevent infinite loops
    setTimeout(() => {
      try {
        const sortedOffsets = computeAdjustedStripOffsets(data, newTrialState);
        if (!sortedOffsets) {
          return;
        }

        // Clear cache to force redraw
        stripLinesCache.clear();

        // Only update state - let the next updateMapData call handle the map update
        setNewTrialState((prevState: any) => ({
          ...prevState,
          stripsData: {
            ...prevState.stripsData,
            offsets: sortedOffsets,
          },
        }));
      } catch (error) {
        console.error("❌ Error adjusting strip lines:", error);
      } finally {
        isAdjusting = false;
      }
    }, 0); // Execute on next tick to break the execution chain
  };
}

/** True when the cached strip render is still valid and nothing forces a recompute. */
function shouldSkipStripRecompute(
  cacheKey: string,
  plotsNeedRecalculation: boolean | undefined,
  plotsMissingStripProperties: boolean | undefined
): boolean {
  return (
    stripLinesCache.has(cacheKey) &&
    !plotsNeedRecalculation &&
    !plotsMissingStripProperties
  );
}

/** Applies a successful addStripLinesToMap result: updates areas/geometry state and caches. */
function applyStripLinesResult(
  temp: any,
  cacheKey: string,
  newTrialState: NewTrialStateInterface,
  setNewTrialState: any
): void {
  if (!temp?.strips || temp.strips.length === 0) return;

  // Update trial state with strip area data AND geometry preservation
  const stripDetails = temp.strips.map((strip: any) => ({
    index: strip.index,
    area: strip.area,
    stripLength: strip.stripLength,
    stripWidth: strip.stripWidth,
  }));
  const areas = temp.strips.map((strip: any) => strip.area);

  // Only update state if the areas have actually changed
  const currentAreas = newTrialState.stripsData?.areas;
  const areasChanged =
    !currentAreas ||
    currentAreas.length !== areas.length ||
    currentAreas.some(
      (area: number, index: number) => Math.abs(area - areas[index]) > 0.01
    );

  if (areasChanged) {
    // Prepare geometry updates for strip plots
    const stripGeometryUpdates = temp.strips.map((strip: any) => ({
      stripIndex: strip.index,
      geometry: strip.geometry,
      area: strip.area,
      length: strip.stripLength,
      width: strip.stripWidth,
    }));

    setNewTrialState((prevState: any) => {
      // Only create stripDetails if user has applied from popup
      const shouldCreateStripDetails =
        prevState.stripsData?.appliedFromPopup === true;

      const updatedState = {
        ...prevState,
        stripsData: {
          ...prevState.stripsData,
          areas,
          ...(shouldCreateStripDetails ? { stripDetails } : {}),
        },
      };

      // Update trial plots with strip geometries if we have strip plots
      if (stripGeometryUpdates.length > 0) {
        try {
          const updatedTrialPlots = updateTrialPlotsWithStripGeometries(
            updatedState.trial_plots,
            stripGeometryUpdates
          );

          if (updatedTrialPlots && Array.isArray(updatedTrialPlots)) {
            // Mark all strip plots as having updated geometry from strips
            updatedTrialPlots.forEach((tp: any) => {
              if (tp.plot?.isStripPlot) {
                tp.plot.geometrySource = "strip"; // Mark as updated from strip calculation
              }
            });
            updatedState.trial_plots = updatedTrialPlots;
          }
        } catch (error) {
          console.error(
            "😱 Error updating trial plots with strip geometries:",
            error
          );
          // Continue without geometry update to prevent crashes
        }
      }

      return updatedState;
    });
  }

  // Add to cache after successful processing
  stripLinesCache.add(cacheKey);
}

interface RecomputeStripLinesParams {
  map: mapboxgl.Map | null;
  newTrialState: NewTrialStateInterface;
  geoJsonData: PolygonGeoJSON;
  setNewTrialState: any;
  current: number;
  abLine: any;
  cacheKey: string;
  plotsNeedRecalculation: boolean | undefined;
  plotsMissingStripProperties: boolean | undefined;
  hasValidStripsMode: boolean;
}

/** Recomputes and redraws strip lines for the currently-selected AB line. */
function recomputeStripLines({
  map,
  newTrialState,
  geoJsonData,
  setNewTrialState,
  current,
  abLine,
  cacheKey,
  plotsNeedRecalculation,
  plotsMissingStripProperties,
  hasValidStripsMode,
}: RecomputeStripLinesParams): void {
  if (plotsNeedRecalculation) {
    stripLinesCache.delete(cacheKey); // Remove from cache to force recalculation
  }
  if (plotsMissingStripProperties) {
    stripLinesCache.delete(cacheKey); // Remove from cache to force recalculation
    markPlotsAsStripPlots(setNewTrialState, hasValidStripsMode);
  }

  // Create a ref object that the function expects
  const mapRef = { current: map };
  const handleStripLineAdjust = createStripLineAdjustHandler(
    newTrialState,
    setNewTrialState
  );

  try {
    const currentStripOffsets = resolveInitialStripOffsets(
      abLine,
      newTrialState
    );
    const temp = addStripLinesToMap(
      mapRef,
      abLine as any, // Type cast to handle type mismatch
      currentStripOffsets, // Use the offsets from trial state
      geoJsonData,
      "demo-strip-lines",
      {
        lineColor: "#000000",
        lineWidth: 2,
        stripOpacity: 0.3,
        stripColors: [
          "#ff000060",
          "#00ff0060",
          "#0000ff60",
          "#ffff0060",
          "#ff00ff60",
        ],
        // Single source of strip number labels (1..N, repeated on every
        // disjoint piece of a MultiPolygon strip). Shown on both the Strip
        // Design and Rates screens.
        showLabels: true,
        // Only show strip controls on Plot Design step (step 1)
        onStripLineAdjust: current === 1 ? handleStripLineAdjust : undefined,
      }
    );

    applyStripLinesResult(temp, cacheKey, newTrialState, setNewTrialState);
  } catch (error) {
    console.error("❌ Error calling addStripLinesToMap:", error);
    if (error instanceof Error) {
      console.error("❌ Error stack:", error.stack);
    }
  }
}

/**
 * Draws/clears/recomputes strip lines for the currently-selected AB line, or
 * logs and no-ops when there is no AB line to anchor strips to.
 */
function processStripLines(
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  geoJsonData: PolygonGeoJSON,
  setNewTrialState: any,
  current: number
): void {
  if (!newTrialState?.ab_line || newTrialState.ab_line.length === 0) {
    console.error("❌ No AB line found in newTrialState.ab_line[0]");
    return;
  }

  // Only draw strips once the user has clicked Apply in the StripsPopup
  if (
    !newTrialState.stripsData ||
    !newTrialState.stripsData.offsets ||
    newTrialState.stripsData.offsets.length === 0 ||
    !newTrialState.stripsData.appliedFromPopup
  ) {
    const mapRef = { current: map };
    clearStripLinesFromMap(mapRef);
    return;
  }

  // Get the AB line for strips based on consolidated stripsData
  const selectedAbLineIndex =
    newTrialState.stripsData?.selectedAbLineIndex ?? 0;
  const abLine =
    newTrialState.ab_line[selectedAbLineIndex] || newTrialState.ab_line[0];

  // Only process strip lines if AB line or strips data has changed. Include
  // current step in cache key to ensure controls are redrawn when step changes.
  const currentOffsets = newTrialState.stripsData?.offsets;
  const cacheKey = `${abLine.id}-${JSON.stringify(
    currentOffsets
  )}-step${current}`;

  // Check if plots need recalculation (when geometrySource is 'manual' for strip plots)
  const plotsNeedRecalculation = newTrialState.trial_plots?.some(
    (tp: any) => tp.plot?.isStripPlot && tp.plot?.geometrySource === "manual"
  );

  // Force processing if we're in strips mode but plots are missing strip properties
  const hasValidStripsMode = newTrialState.stripsData?.isStripMode === true;
  const plotsMissingStripProperties =
    hasValidStripsMode &&
    newTrialState.trial_plots?.some(
      (tp: any) => tp.plot && tp.plot.isStripPlot !== true
    );

  if (
    shouldSkipStripRecompute(
      cacheKey,
      plotsNeedRecalculation,
      plotsMissingStripProperties
    )
  ) {
    return;
  }

  recomputeStripLines({
    map,
    newTrialState,
    geoJsonData,
    setNewTrialState,
    current,
    abLine,
    cacheKey,
    plotsNeedRecalculation,
    plotsMissingStripProperties,
    hasValidStripsMode,
  });
}

/**
 * Hides a trial plot's (and its replicants') drag/rotate markers once it is no
 * longer the actively-edited step-1 plot (unless strips mode keeps them live).
 */
function removeMarkersIfHiddenStep(
  trialItem: TrialPlot,
  current: number,
  isStripMode: boolean
): void {
  if (current === 1 && !isStripMode) return;

  const trialPlotId = trialItem?.plot?.id;
  const rotationMarkerId = `rotation-marker-${trialPlotId as number}`;
  const envelopeCenterMarkerId = `envelope-center-marker-${
    trialPlotId as number
  }`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }
  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }

  const replicantIds = trialItem?.replicants?.map((rep) => rep.id);
  replicantIds?.forEach((replicantId) => {
    const envelopeCenterMarkerIdReplicant = `envelope-center-marker-replicant-${replicantId}`;
    const rotationMarkerIdReplicant = `rotation-marker-replicant-${replicantId}`;

    if (markersForTrialPlot[envelopeCenterMarkerIdReplicant]) {
      markersForTrialPlot[envelopeCenterMarkerIdReplicant].remove();
    }
    if (markersForTrialPlot[rotationMarkerIdReplicant]) {
      markersForTrialPlot[rotationMarkerIdReplicant].remove();
    }
  });
}

interface PlotDesignLayersParams {
  map: mapboxgl.Map | null;
  newTrialState: NewTrialStateInterface;
  geoJsonData: PolygonGeoJSON;
  fieldData: FieldDataInterface[];
  setNewTrialState: any;
  createCustomMarkerElement: (iconUrl: string) => HTMLDivElement;
  getAbLineColor: any;
  trialProtocolData: any;
  current: number;
  plotsValidationData: PlotsValidationData;
  setPlotsValidationData: any;
  checkPlotBoundaries: any;
}

/** Draws the buffer-zone, AB-line, and trial-plot (+ strip) layers for the current step. */
function renderPlotDesignLayers({
  map,
  newTrialState,
  geoJsonData,
  fieldData,
  setNewTrialState,
  createCustomMarkerElement,
  getAbLineColor,
  trialProtocolData,
  current,
  plotsValidationData,
  setPlotsValidationData,
  checkPlotBoundaries,
}: PlotDesignLayersParams): void {
  if (newTrialState.buffer_zone) {
    addBufferZoneLayer(map, fieldData, newTrialState, geoJsonData);
  }

  if (newTrialState.ab_line) {
    addABLinesToMap(map, newTrialState, geoJsonData, {
      setNewTrialState,
      createCustomMarkerElement,
      getAbLineColor,
      currentStep: current,
      trialProtocolData,
    });
  }

  if (newTrialState.trial_plots) {
    addTrialPlotToMap(
      map,
      newTrialState,
      geoJsonData,
      createCustomMarkerElement,
      plotsValidationData,
      setPlotsValidationData,
      checkPlotBoundaries
    );
    processStripLines(
      map,
      newTrialState,
      geoJsonData,
      setNewTrialState,
      current
    );
  }

  const isStripMode = newTrialState.stripsData?.isStripMode === true;
  newTrialState?.trial_plots?.forEach((trialItem: TrialPlot) => {
    removeMarkersIfHiddenStep(trialItem, current, isStripMode);
  });
}

/** True when there's a map and at least one productivity-zone feature to draw. */
function hasProductivityZoneFeatures(
  map: mapboxgl.Map | null,
  productivityZonesData: ProductivityZonesInterface
): boolean {
  return !!(
    map &&
    productivityZonesData?.results[0]?.predictions[0]?.productivity_zones
      .features.length > 0
  );
}

interface ProductivityZonesOverlayParams {
  productivityZonesData: ProductivityZonesInterface;
  autozoneOpacity: number;
  autozoneColorPalette: string;
  setIsAutozonesLoaded: any;
  soilOperationsData: any;
}

/** Draws autozones + soil overlays for the current view. */
function applyProductivityZonesOverlay(
  map: mapboxgl.Map,
  {
    productivityZonesData,
    autozoneOpacity,
    autozoneColorPalette,
    setIsAutozonesLoaded,
    soilOperationsData,
  }: ProductivityZonesOverlayParams
): void {
  addAutoZonesLayer(
    map,
    productivityZonesData,
    autozoneOpacity,
    autozoneColorPalette
  );
  setIsAutozonesLoaded(true);

  applyOrRemoveSoilLayer(map, soilOperationsData);
}

/** Colours the rendered plot cells by rate/dosage, when data is present. */
function applyRatesAndDosageOverlay(
  currentRatesDosage: any,
  map: mapboxgl.Map | null,
  trialProducts: any,
  isFertilizerTabActive: boolean,
  activeRatesTab: string,
  bioApplicationIndex: number
): void {
  const ratesDosagesArray = currentRatesDosage?.rates_and_dosages;
  if (!ratesDosagesArray || ratesDosagesArray.length === 0) return;

  addRatesAndDosageToMap(
    ratesDosagesArray,
    map,
    trialProducts,
    currentRatesDosage?.unit_of_rate,
    isFertilizerTabActive,
    activeRatesTab,
    bioApplicationIndex
  );
}

/** Applies the nutrient blue scale to the Summary map's plot rows. */
function addSummaryNutrientColors(currentRatesDosage: any): any {
  const ratesAndDosages = currentRatesDosage?.rates_and_dosages;
  const getColor = createNutrientPlotColorScale(
    ratesAndDosages?.flatMap((group: RatesDosageDataInterface[]) =>
      group.map((item) => item.fertiliser_rate)
    ) ?? []
  );

  return {
    ...currentRatesDosage,
    rates_and_dosages: ratesAndDosages?.map(
      (group: RatesDosageDataInterface[]) =>
        group.map((item) => ({
          ...item,
          mapFillColor: getColor(item.fertiliser_rate),
        }))
    ),
  };
}

function applyStepRatesAndDosageOverlay(
  current: number,
  currentRatesDosage: any,
  map: mapboxgl.Map | null,
  trialProducts: any,
  isFertilizerTabActive: boolean,
  activeRatesTab: string,
  bioApplicationIndex: number
): void {
  const displayRatesDosage =
    current === 3
      ? addSummaryNutrientColors(currentRatesDosage)
      : currentRatesDosage;
  applyRatesAndDosageOverlay(
    displayRatesDosage,
    map,
    trialProducts,
    isFertilizerTabActive,
    activeRatesTab,
    bioApplicationIndex
  );
}

/** Draws the soil overlay image when soil data is present, else removes it. */
function applyOrRemoveSoilLayer(
  map: mapboxgl.Map,
  soilOperationsData: any
): void {
  if (!soilOperationsData) {
    removeSoilImageLayer(map);
    return;
  }
  addSoilImageLayer(
    map,
    {
      geoJsonData: soilOperationsData?.geoJsonData,
      zoneData: soilOperationsData?.zoneData,
    },
    soilOperationsData?.palette || "default",
    soilOperationsData?.opacity || 60
  );
}

function moveFieldLabelsToFront(map: mapboxgl.Map | null): void {
  if (!map || !map.getLayer("polygon-name")) return;
  try {
    map.moveLayer("polygon-name");
  } catch (error) {
    console.warn("Unable to move field labels above map layers:", error);
  }
}

export interface UpdateMapDataParams {
  map: mapboxgl.Map | null;
  mapObjectRef: React.MutableRefObject<mapboxgl.Map | null>;
  current: number;
  isFertilizerTabActive: boolean;
  activeRatesTab: string;
  fieldData: FieldDataInterface[];
  newTrialState: NewTrialStateInterface;
  setNewTrialState: any;
  createCustomMarkerElement: (iconUrl: string) => HTMLDivElement;
  geoJsonData: PolygonGeoJSON;
  plotsValidationData: PlotsValidationData;
  setPlotsValidationData: any;
  autozoneRequestStatus: autozoneRequestStatus;
  productivityZonesData: ProductivityZonesInterface;
  isAutozonesLoaded: boolean;
  setIsAutozonesLoaded: any;
  autozoneOpacity: number;
  autozoneColorPalette: string;
  fieldOperationsData: any;
  stripsData: any;
  setStripsData: any;
  updateStripsData: any;
  soilOperationsData: any; // <-- soil data object (GeoJSON or raster)
  ratesDosageParam?: any;
  soilColorPalete?: string;
  soilOpacity?: any;
  soilImageData?: { image_location: string; bbox: number[][] };
}

export const useMapComponent = (): {
  createCustomMarkerElement: (iconUrl: string) => HTMLDivElement;

  updateMapData: (params: UpdateMapDataParams) => void;
  updateABWidth: (
    abItem: AbLine,
    sourceId: string,
    map: mapboxgl.Map,
    bbox: any,
    polygonGeoJSON: PolygonGeoJSON,
    trialProtocolData?: any
  ) => void;

  checkPlotBoundaries: typeof checkPlotBoundaries;
  updateStripsData: (
    map: mapboxgl.Map | null,
    newTrialState: NewTrialStateInterface,
    polygonGeoJSON: PolygonGeoJSON,
    setNewTrialState: any,
    currentStep?: number,
    setStripsData?: any,
    stripsData?: any
  ) => void;
  clearStripLinesCache: () => void;
  clearStripLinesFromMap: (
    mapRef?: React.MutableRefObject<mapboxgl.Map | null>
  ) => void;
} => {
  const createCustomMarkerElement = (
    iconUrl: string,
    abItemid?: number
  ): HTMLDivElement => {
    const markerElement = document.createElement("div");
    markerElement.className = "custom-marker";
    markerElement.style.backgroundImage = `url(${iconUrl})`;
    markerElement.style.width = "30px";
    markerElement.style.height = "30px";
    markerElement.style.backgroundSize = "cover";
    return markerElement;
  };

  const { ratesDosage, trialProtocolData, bioApplicationIndex } =
    useNewTrialState();
  const { getAbLineColor } = usePlotCardState();
  const trialProducts =
    trialProtocolData?.product_details?.seeds?.products ?? [];

  // Flag to prevent multiple simultaneous executions
  let isUpdatingMapData = false;

  const updateMapData = ({
    map,
    mapObjectRef,
    current,
    isFertilizerTabActive,
    activeRatesTab,
    fieldData,
    newTrialState,
    setNewTrialState,
    createCustomMarkerElement,
    geoJsonData,
    plotsValidationData,
    setPlotsValidationData,
    autozoneRequestStatus,
    productivityZonesData,
    isAutozonesLoaded,
    setIsAutozonesLoaded,
    autozoneOpacity,
    autozoneColorPalette,
    fieldOperationsData,
    stripsData,
    setStripsData,
    updateStripsData,
    soilOperationsData, // <-- soil data object (GeoJSON or raster)
    ratesDosageParam,
    soilColorPalete,
    soilOpacity,
    soilImageData,
  }: UpdateMapDataParams): void => {
    // Track step changes to clear cache when step changes
    if (previousStep !== undefined && previousStep !== current) {
      stripLinesCache.clear();
    }
    previousStep = current;

    // Use passed parameter or fallback to hook (for backward compatibility)
    const currentRatesDosage = ratesDosageParam || ratesDosage;

    // Prevent multiple simultaneous executions
    if (isUpdatingMapData) {
      return;
    }

    isUpdatingMapData = true;

    // Store module-level references for snapPlotsToAbLine and checkAndReportAlignment
    if (map) moduleMap = map;
    moduleNewTrialState = newTrialState;

    // Perform an initial alignment check when entering plot-design (step 1)
    // so the banner reflects the current alignment state without requiring a drag event.
    if (current === 1) {
      checkAndReportAlignment();
    }

    try {
      if ([1, 2, 3].includes(current)) {
        renderPlotDesignLayers({
          map,
          newTrialState,
          geoJsonData,
          fieldData,
          setNewTrialState,
          createCustomMarkerElement,
          getAbLineColor,
          trialProtocolData,
          current,
          plotsValidationData,
          setPlotsValidationData,
          checkPlotBoundaries,
        });
      }

      if (hasProductivityZoneFeatures(map, productivityZonesData)) {
        applyProductivityZonesOverlay(map as mapboxgl.Map, {
          productivityZonesData,
          autozoneOpacity,
          autozoneColorPalette,
          setIsAutozonesLoaded,
          soilOperationsData,
        });
      }

      if (map && fieldOperationsData) {
        addFieldOperationsLayer(map as mapboxgl.Map, fieldOperationsData);
      }

      // Re-run the alignment check now that the AB-line/machinery-grid and
      // trial-plot layers above have actually been added to the map.
      if (current === 1) {
        checkAndReportAlignment();
      }

      if ([2, 3].includes(current)) {
        applyStepRatesAndDosageOverlay(
          current,
          currentRatesDosage,
          map,
          trialProducts,
          isFertilizerTabActive,
          activeRatesTab,
          bioApplicationIndex
        );
      }

      // Field name labels always appear on top of all other layers.
      moveFieldLabelsToFront(map);
    } catch (error) {
      console.error("Error in updateMapData:", error);
    } finally {
      isUpdatingMapData = false;
    }
  };

  return {
    createCustomMarkerElement,
    updateMapData,
    updateABWidth,
    checkPlotBoundaries,
    updateStripsData,
    clearStripLinesCache,
    clearStripLinesFromMap,
  };
};

export const initializeMap = (
  map: mapboxgl.Map | null,
  fieldData: FieldDataInterface[],
  trialInfo: TrialInfoInterface,
  newTrialState: NewTrialStateInterface,
  current: number,
  handleFieldSelection: any
): any => {
  if (!map || !fieldData || fieldData.length === 0) {
    return undefined;
  }
  const featuresWithNames = fieldData.map((item: FieldDataInterface) => {
    const isSelected = trialInfo?.field_ids?.includes(item.id);
    return {
      ...item,
      properties: {
        name: `${item.name}`,
        fieldId: item.id,
        selected: isSelected,
      },
      type: "Feature",
    };
  });

  const selectedPolygon = featuresWithNames.find(
    (item: FieldDataInterface) => item.properties?.selected
  );

  const selectedPolygons = selectedPolygon ? [selectedPolygon] : [];

  const polygonGeoJSON: any = {
    type: "FeatureCollection",
    features: current === 0 ? featuresWithNames : selectedPolygons,
  };

  const geojsonFieldDataSource = map.getSource(
    "geojson-field-data"
  ) as mapboxgl.GeoJSONSource;
  geojsonFieldDataSource?.setData(polygonGeoJSON);

  const centroidFeatures = (
    current === 0 ? featuresWithNames : selectedPolygons
  )
    .map((feature: any) => {
      try {
        const pointOnFeature = turf.pointOnFeature(feature as any);
        return {
          type: "Feature",
          geometry: pointOnFeature.geometry,
          properties: {
            name: feature.properties?.name,
            fieldId: feature.properties?.fieldId,
            selected: feature.properties?.selected,
          },
        };
      } catch (error) {
        console.error("Error calculating point on feature:", error);
        return null;
      }
    })
    .filter(Boolean);

  const centroidGeoJSON: any = {
    type: "FeatureCollection",
    features: centroidFeatures,
  };

  const geojsonFieldLabelsSource = map.getSource(
    "geojson-field-labels"
  ) as mapboxgl.GeoJSONSource;
  geojsonFieldLabelsSource?.setData(centroidGeoJSON);

  if (current === 0) {
    map.on("click", "polygon-fill", (e: mapboxgl.MapLayerMouseEvent) => {
      const clickedFeature = e.features?.[INDEX_ZERO];
      // A measure point or a coordinate copy is not a field pick, even though
      // Mapbox hands the same click to both listeners.
      if (isMapToolCapturingClicks()) {
        return;
      }
      if (!clickedFeature || !allowFieldInputChange(trialInfo, newTrialState)) {
        return;
      }
      const clickedFieldId = clickedFeature.properties?.fieldId;

      handleFieldSelection(clickedFieldId);
    });
  }

  // Ensure field name labels always appear on top of all other layers
  if (map.getLayer("polygon-name")) {
    try {
      map.moveLayer("polygon-name");
    } catch (e) {
      // Layer might not exist or can't be moved
    }
  }

  return polygonGeoJSON;
};

const addTrialPlotToMap = (
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  polygonGeoJSON: PolygonGeoJSON,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  plotsValidationData: PlotsValidationData,
  setPlotsValidationData: any,
  checkPlotBoundaries: any
): void => {
  if (!map || !newTrialState) return;

  if (previousTrialPlots) {
    const previousIds = new Set(
      previousTrialPlots.map((trialItem) => trialItem.plot.id)
    );

    const currentIds = new Set(
      newTrialState.trial_plots.map((trialItem) => trialItem.plot.id)
    );

    previousIds.forEach((id) => {
      if (!currentIds.has(id)) {
        removeTrialPlotLayersAndSources(
          map,
          id as number,
          previousTrialPlots as any
        );
        removeMarkersForTrialPlot(id as number);
      }
    });

    previousTrialPlots.forEach((previousTrialItem: TrialPlot) => {
      const newTrialItem = newTrialState.trial_plots.find(
        (item) => item.plot.id === previousTrialItem.plot.id
      );

      // Check if the number of plots (rows × columns) has changed
      if (newTrialItem) {
        const previousPlotCount =
          previousTrialItem.plot.rows * previousTrialItem.plot.columns;
        const newPlotCount = newTrialItem.plot.rows * newTrialItem.plot.columns;

        if (previousPlotCount > newPlotCount) {
          // Remove labels for plots that no longer exist
          const trialPlotId = previousTrialItem.plot.id as number;
          for (let index = newPlotCount; index < previousPlotCount; index++) {
            const cellLayerId = `${trialPlotId}-${index}`;
            const labelSourceId = `${cellLayerId}-label`;
            const labelLayerId = `${labelSourceId}-label-plot`;

            if (map.getLayer(labelLayerId)) {
              map.removeLayer(labelLayerId);
            }
            if (map.getSource(labelSourceId)) {
              map.removeSource(labelSourceId);
            }
          }
        }
      }

      if (previousTrialItem.replicants) {
        previousTrialItem.replicants.forEach((replicant, index) => {
          const stillExists = newTrialItem?.replicants?.some(
            (newReplicant) => newReplicant.id === replicant.id
          );

          if (!stillExists) {
            removeReplicantLayersAndSources(
              map,
              previousTrialItem.plot.id as number,
              replicant.id as number,
              index
            );
          } else if (newTrialItem) {
            // Check if the number of plots has changed in the replicant
            const previousPlotCount =
              previousTrialItem.plot.rows * previousTrialItem.plot.columns;
            const newPlotCount =
              newTrialItem.plot.rows * newTrialItem.plot.columns;

            if (previousPlotCount > newPlotCount) {
              // Remove labels for replicant plots that no longer exist
              const trialPlotId = previousTrialItem.plot.id as number;
              const cloneIndex = index;
              for (
                let plotIndex = newPlotCount;
                plotIndex < previousPlotCount;
                plotIndex++
              ) {
                const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${plotIndex}`;
                const labelSourceId = `${cellLayerId}-label`;
                const labelLayerId = `${labelSourceId}-label-plot`;

                if (map.getLayer(labelLayerId)) {
                  map.removeLayer(labelLayerId);
                }
                if (map.getSource(labelSourceId)) {
                  map.removeSource(labelSourceId);
                }
              }
            }
          }
        });
      }
    });
  }

  newTrialState.trial_plots.forEach(
    (trialItem: TrialPlot, plotIndex: number) => {
      const rows = trialItem.plot.rows;
      const columns = trialItem.plot.columns;
      const plotWidth = trialItem.plot.plotWidth;
      const plotHeight = trialItem.plot.plotHeight;
      const columnSize = trialItem.plot.column_size as number;
      const rowSize = trialItem.plot.row_size as number;
      const plotGeoJson = trialItem?.plot?.geojson;
      const trialPlotId = trialItem.plot.id as number;
      const randomPointsSourceId = `random-points-${trialPlotId}`;
      const envelopeSourceId = `trial-plot-envelope-${trialPlotId}`;
      const gridSourceId = `grid-of-trial-plots-${trialPlotId}`;
      const gridLayerId = `grid-layer-${trialPlotId}`;
      const transParentLayerId = `transparent-layer-${trialPlotId}`;

      createNewTrialPlots({
        map,
        trialPlotId,
        polygonGeoJSON,
        randomPointsSourceId,
        envelopeSourceId,
        gridSourceId,
        gridLayerId,
        rows,
        columns,
        plotWidth: plotWidth as number,
        plotHeight: plotHeight as number,
        columnSize,
        rowSize,
        plotGeoJson,
        newTrialState,
        createCustomMarkerElement,
        visible: trialItem.plot.visible as boolean,
        id: plotIndex, // Use the index in the array instead of the plot ID
        transParentLayerId,
        trialItem,
        plotsValidationData,
        setPlotsValidationData,
        checkPlotBoundaries,
      });
    }
  );
  previousTrialPlots = [...newTrialState.trial_plots];
};

const removeTrialPlotLayersAndSources = (
  map: mapboxgl.Map,
  id: number,
  previousTrialPlots: TrialPlot[]
): void => {
  const randomPointsSourceId = `random-points-${id}`;
  const envelopeSourceId = `trial-plot-envelope-${id}`;
  const envelopeLayerId = `envelope-layer-${id}`;
  const gridSourceId = `grid-of-trial-plots-${id}`;
  const gridLayerId = `grid-layer-${id}`;
  const transParentLayerId = `transparent-layer-${id}`;

  if (map.getLayer(gridLayerId)) {
    map.removeLayer(gridLayerId);
  }

  // Remove all layers and sources related to this trial plot
  map?.getStyle()?.layers?.forEach((layer) => {
    if (layer?.id?.includes(id?.toString())) {
      map?.removeLayer(layer?.id);
    }
  });

  // Remove plot label HTML markers
  const trialItem = previousTrialPlots.find((item) => item.plot.id === id);
  if (trialItem) {
    const rows = trialItem.plot.rows;
    const columns = trialItem.plot.columns;
    const totalPlots = rows * columns;

    removePlotLabelMarkers(id, totalPlots);
  }

  // Remove group label layer and source
  const groupLabelSourceId = `${gridSourceId}-group-label`;
  const groupLabelLayerId = `${groupLabelSourceId}-label-group`;
  if (map.getLayer(groupLabelLayerId)) {
    map.removeLayer(groupLabelLayerId);
  }
  if (map.getSource(groupLabelSourceId)) {
    map.removeSource(groupLabelSourceId);
  }

  if (map.getSource(gridSourceId)) {
    map.removeSource(gridSourceId);
  }
  if (map.getLayer(envelopeLayerId)) {
    map.removeLayer(envelopeLayerId);
  }
  if (map.getSource(envelopeSourceId)) {
    map.removeSource(envelopeSourceId);
  }
  if (map.getSource(randomPointsSourceId)) {
    map.removeSource(randomPointsSourceId);
  }
  if (map.getSource(transParentLayerId)) {
    map.removeSource(transParentLayerId);
  }

  const markerId = `drag-marker-${id}`;
  if (markersForTrialPlot[markerId]) markersForTrialPlot[markerId].remove();

  const marker2Id = `rotate-marker-${id}`;
  if (markersForTrialPlot[marker2Id]) markersForTrialPlot[marker2Id].remove();

  const marker3Id = `rotation-marker-${id}`;
  if (markersForTrialPlot[marker3Id]) markersForTrialPlot[marker3Id].remove();

  if (trialItem?.replicants) {
    trialItem.replicants.forEach((replicant, index) => {
      removeReplicantLayersAndSources(
        map,
        trialItem.plot.id as number,
        replicant.id as number,
        index
      );
    });
  }
};

const removeReplicantLayersAndSources = (
  map: mapboxgl.Map,
  trialPlotId: number,
  id: number,
  index: number
): void => {
  const cloneGridSourceId = `cloned-grid-of-trial-plots-${index}-${trialPlotId}-${id}`;
  const cloneGridLayerId = `cloned-grid-layer-${index}-${trialPlotId}-${id}`;
  const envelopeCenterMarkerId = `envelope-center-marker-replicant-${id}`;
  const rotationMarkerId = `rotation-marker-replicant-${id}`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }
  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }

  // Remove all layers related to this replicant
  map?.getStyle()?.layers?.forEach((layer) => {
    if (layer.id.includes(`cloned-${index}-${trialPlotId.toString()}`)) {
      map.removeLayer(layer.id);
    }
  });

  // Remove replicant group label
  const replicantGroupLabelSourceId = `${cloneGridSourceId}-group-label`;
  const replicantGroupLabelLayerId = `${replicantGroupLabelSourceId}-label-group`;
  if (map.getLayer(replicantGroupLabelLayerId)) {
    map.removeLayer(replicantGroupLabelLayerId);
  }
  if (map.getSource(replicantGroupLabelSourceId)) {
    map.removeSource(replicantGroupLabelSourceId);
  }

  if (map.getLayer(cloneGridLayerId)) {
    map.removeLayer(cloneGridLayerId);
  }
  if (map.getSource(cloneGridSourceId)) {
    map.removeSource(cloneGridSourceId);
  }
};

const removeMarkersForTrialPlot = (trialPlotId: number): void => {
  const envelopeCenterMarkerId = `envelope-center-marker-${trialPlotId}`;
  const rotationMarkerId = `rotation-marker-${trialPlotId}`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }

  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }
};

const markerForTrials = (
  map: mapboxgl.Map,
  position: mapboxgl.LngLatLike,
  icon: string,
  id: number,
  createCustomMarkerElement: (icon: string) => HTMLElement
): mapboxgl.Marker => {
  const markerId = `rotate-marker-${id}`;

  const marker = new mapboxgl.Marker({
    draggable: true,
    element: createCustomMarkerElement(icon),
  })
    .setLngLat(position)
    .addTo(map);

  markersForTrialPlot[markerId] = marker;
  return marker;
};

const markerForTrialPlots = (
  map: mapboxgl.Map,
  position: mapboxgl.LngLatLike,
  icon: string,
  id: number,
  createCustomMarkerElement: (icon: string) => HTMLElement
): mapboxgl.Marker => {
  const markerId = `drag-marker-${id}`;

  const marker = new mapboxgl.Marker({
    draggable: true,
    element: createCustomMarkerElement(icon),
  })
    .setLngLat(position)
    .addTo(map);

  markersForTrialPlot[markerId] = marker;
  return marker;
};

function getRotated(
  squareGrid: GeoJSON,
  rotationAngle: number,
  rows: number,
  columns: number
): any {
  const features: any = squareGrid.features;

  const firstPolygon: any = features[0].geometry.coordinates[0];

  const lastPolygon: any =
    features[features.length - 1].geometry.coordinates[0];

  const northWest = firstPolygon[1];
  const northEast = features[columns - 1].geometry.coordinates[0][1];
  const southWest =
    features[features.length - columns].geometry.coordinates[0][3];
  const southEast = lastPolygon[0];

  const corners = [northWest, northEast, southEast, southWest];

  const center = turf.center(squareGrid).geometry.coordinates;
  const rotatedCorners = corners.map((corner) =>
    turf.transformRotate(turf.point(corner), rotationAngle, { pivot: center })
  );
  return rotatedCorners[0].geometry.coordinates;
}

function getNorthWestCorner(
  squareGrid: GeoJSON,
  rows: number,
  columns: number
): [number, number] {
  const features: any = squareGrid.features;
  const firstPolygon: any = features[0].geometry.coordinates[0];
  return firstPolygon[1];
}

// Plot Design recentres the camera on a plot whenever it is shown; a hosting
// screen that only displays plots (Rates & Dosage) must keep the camera where
// the user left it, since these redraws also run on unrelated re-renders.
let isPlotRecenterSuppressed = false;

export const setPlotRecenterSuppressed = (suppressed: boolean): void => {
  isPlotRecenterSuppressed = suppressed;
};

const setLayerVisibilityOfTrialPlots = (
  map: mapboxgl.Map,
  gridLayerId: string,
  visible: boolean,
  id: number,
  gridGeoJSON: any,
  gridSourceId: string,
  transParentLayerId: string
): void => {
  if (visible) {
    map.setLayoutProperty(gridLayerId, "visibility", "visible");
    map.setLayoutProperty(transParentLayerId, "visibility", "visible");
    // gridRotations is no longer populated for plots (see top-of-file note),
    // so only the stored position ever applies here.
    if (gridPositions[id] && !isPlotRecenterSuppressed) {
      map.setCenter([gridPositions[id].lng, gridPositions[id].lat]);
    }
  } else {
    map.setLayoutProperty(gridLayerId, "visibility", "none");
    map.setLayoutProperty(transParentLayerId, "visibility", "none");
  }

  const markerIds = [
    `envelope-center-marker-${id}`,
    `drag-marker-${id}`,
    `rotate-marker-${id}`,
  ];
  markerIds.forEach((markerId) => {
    if (markersForTrialPlot[markerId]) {
      markersForTrialPlot[markerId].getElement().style.display = visible
        ? "block"
        : "none";
    }
  });
};

const addLabelToMap = (
  map: mapboxgl.Map,
  sourceId: string,
  label: string,
  position: "top" | "bottom" | "left" | "right" | "center",
  offset: [number, number],
  type: string,
  size: number
) => {
  const labelLayerId = `${sourceId}-label-${type}`;

  // Set default values for different label types
  const defaultOffsets: Record<string, [number, number]> = {
    group: [0, -0.2], // Group labels (A, B, C, etc.) slightly above the plot
    plot: [0, 0], // Plot labels (1, 2, 3, etc.) centered
  };

  const defaultSizes: Record<string, number> = {
    group: 14, // Larger for group labels
    plot: 12, // Normal size for plot labels
  };

  // Use the provided values or defaults
  const finalOffset = offset || defaultOffsets[type] || [0, 0];
  const finalSize = size || defaultSizes[type] || 12;

  // Remove existing label layer if it exists
  if (map.getLayer(labelLayerId)) {
    map.removeLayer(labelLayerId);
  }

  // Add the label layer at the top of all layers
  const style = map.getStyle();
  const layers = style?.layers || [];
  const topLayerId = layers.at(-1)?.id;

  map.addLayer(
    {
      id: labelLayerId,
      type: "symbol",
      source: sourceId,
      layout: {
        "text-field": String(label), // Ensure label is treated as a string
        "text-size": finalSize,
        "text-offset": finalOffset,
        "text-anchor": position,
        // "text-allow-overlap": true, // Allow labels to overlap with other map elements
        //  "text-ignore-placement": true, // Prioritize showing these labels
        "symbol-z-order": "source", // Ensure labels appear on top based on source order
      },
      paint: {
        "text-color": "#FFFFFF",
        "text-halo-color": "#000000",
        "text-halo-width": type === "group" ? 2 : 1.5, // Stronger halo for group labels
        "text-opacity": 1,
      },
    },
    topLayerId
  );
};

interface UpdateGroupLabelPositionParams {
  map: mapboxgl.Map;
  baseSourceId: string;
  rotatedGrid: any;
  newTrialState: NewTrialStateInterface;
  trialPlotId: number;
  labelSuffix: string;
  offset: [number, number];
  fontSize: number;
}

/**
 * Updates or creates a group label source/layer for a trial plot or replicant.
 * Shared helper to eliminate duplicate group-label-position code in drag handlers.
 */
const updateGroupLabelPosition = ({
  map,
  baseSourceId,
  rotatedGrid,
  newTrialState,
  trialPlotId,
  labelSuffix,
  offset,
  fontSize,
}: UpdateGroupLabelPositionParams): void => {
  const bounds = turf.bbox(rotatedGrid);
  const center = turf.center(rotatedGrid);
  const labelPoint = turf.point([
    center.geometry.coordinates[0],
    bounds[3] + (bounds[3] - bounds[1]) * 0.01,
  ]);
  const groupLabelSourceId = `${baseSourceId}-group-label`;
  const groupLabelSource = map.getSource(
    groupLabelSourceId
  ) as mapboxgl.GeoJSONSource;
  if (groupLabelSource) {
    groupLabelSource.setData(labelPoint);
  } else {
    map.addSource(groupLabelSourceId, { type: "geojson", data: labelPoint });
    const groupLabelLayerId = `${groupLabelSourceId}-label-group`;
    if (!map.getLayer(groupLabelLayerId)) {
      const plotIndex = newTrialState.trial_plots.findIndex(
        (tp) => tp.plot.id === trialPlotId
      );
      const groupLabel =
        String.fromCodePoint(65 + (plotIndex % 26)) + labelSuffix;
      addLabelToMap(
        map,
        groupLabelSourceId,
        groupLabel,
        "bottom",
        offset,
        "group",
        fontSize
      );
    }
  }
};

/**
 * Updates HTML marker positions for each plot cell after rotation/drag.
 */
export const updatePlotLabelMarkersPosition = (
  rotatedGrid: any,
  trialPlotId: number
): void => {
  rotatedGrid.features.forEach((feature: any, index: number) => {
    const markerId = `plot-label-marker-${trialPlotId}-${index}`;
    const marker = markersForTrialPlot[markerId];
    if (marker) {
      const center = turf.centerOfMass(feature).geometry.coordinates as [
        number,
        number
      ];
      marker.setLngLat(center);
    }
  });
};

/**
 * Removes all HTML plot-label markers for a trial plot from the map and registry.
 * Called when a trial plot grid is deleted or the trial view is exited.
 */
export const removePlotLabelMarkers = (
  trialPlotId: number,
  totalPlots: number
): void => {
  for (let index = 0; index < totalPlots; index++) {
    const markerId = `plot-label-marker-${trialPlotId}-${index}`;
    if (markersForTrialPlot[markerId]) {
      markersForTrialPlot[markerId].remove();
      delete markersForTrialPlot[markerId];
    }
  }
};

/**
 * Remove EVERY HTML marker belonging to a plot — its cell number labels (any
 * count) and its move/rotate handles — from both the map and the registry. This
 * is the single cleanup that runs before a plot is (re)rendered, so no marker can
 * survive at a stale position after a resize, drag, rotate, or any re-render
 * (whatever the previous cell count or handle state). The trailing "-" on the
 * label prefix and the exact-match handle ids avoid colliding with another plot
 * whose id shares a digit prefix (e.g. 123 vs 1234) or with replicant markers
 * (…-replicant-…), which are cleaned separately.
 */
export const removeAllMarkersForPlot = (trialPlotId: number): void => {
  const labelPrefix = `plot-label-marker-${trialPlotId}-`;
  const centerMarkerId = `envelope-center-marker-${trialPlotId}`;
  const rotationMarkerId = `rotation-marker-${trialPlotId}`;
  Object.keys(markersForTrialPlot).forEach((markerId) => {
    if (
      markerId.startsWith(labelPrefix) ||
      markerId === centerMarkerId ||
      markerId === rotationMarkerId
    ) {
      markersForTrialPlot[markerId].remove();
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete markersForTrialPlot[markerId];
    }
  });
};

/**
 * Updates or creates GeoJSON label sources/layers for each replicant plot cell.
 */
const updateReplicantPlotLabels = (
  map: mapboxgl.Map,
  rotatedGrid: any,
  cloneIndex: number,
  trialPlotId: number
): void => {
  rotatedGrid.features.forEach((feature: any, index: number) => {
    const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}`;
    const labelSourceId = `${cellLayerId}-label`;
    const labelSource = map.getSource(labelSourceId) as mapboxgl.GeoJSONSource;
    if (labelSource) {
      labelSource.setData(turf.featureCollection([feature]));
    } else {
      map.addSource(labelSourceId, {
        type: "geojson",
        data: turf.featureCollection([feature]),
      });
      addLabelToMap(
        map,
        labelSourceId,
        `${index + 1}`,
        "center",
        [0, 0],
        "plot",
        12
      );
    }
  });
};

/**
 * Creates a plot label marker and adds it to the map and registry.
 * Side effects: adds marker to the map and stores it in markersForTrialPlot registry.
 */
export const createPlotLabelMarker = (
  map: mapboxgl.Map,
  lngLat: [number, number],
  label: string,
  trialPlotId: number,
  index: number
): void => {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.cssText =
    "color:#fff;font-size:12px;font-weight:600;pointer-events:none;" +
    "text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;";

  const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
    .setLngLat(lngLat)
    .addTo(map);

  const markerId = `plot-label-marker-${trialPlotId}-${index}`;
  markersForTrialPlot[markerId] = marker;
};

/**
 * Build one map feature per strip for a strip group, each a Polygon or
 * MultiPolygon (mapbox fills all subparts). The geometries are recomputed from
 * the saved offsets so we ALWAYS get exactly one cell per strip — even when
 * editing a loaded trial. The saved geometry is a GeometryCollection (one cell
 * per strip), but we recompute as the primary source so it works even before the
 * backend round trip. Each feature's id matches a regular plot cell
 * (`${plotId}-0-${col}`), so the rates step colours each strip independently.
 */
const buildStripCellFeatures = (
  trialItem: any,
  trialPlotId: number,
  polygonGeoJSON: PolygonGeoJSON,
  newTrialState: NewTrialStateInterface
): any[] => {
  const makeFeature = (geometry: any, stripIndex: number) => ({
    type: "Feature" as const,
    geometry: geometry?.type === "Feature" ? geometry.geometry : geometry,
    properties: {
      id: `${trialPlotId}-0-${stripIndex}`,
      color: RGBA_PLOT_FILL,
      stripIndex,
    },
  });

  // Primary: recompute the strips from the saved offsets (deterministic).
  try {
    const offsets = newTrialState?.stripsData?.offsets;
    const abIndex = newTrialState?.stripsData?.selectedAbLineIndex ?? 0;
    const stripAbLine =
      newTrialState?.ab_line?.[abIndex] ?? newTrialState?.ab_line?.[0];
    const fieldGeometry = polygonGeoJSON?.features?.[0]?.geometry;
    if (
      Array.isArray(offsets) &&
      offsets.length > 0 &&
      (stripAbLine?.geojson?.geometry?.coordinates?.length ?? 0) >= 2 &&
      fieldGeometry
    ) {
      const lines = generateParallelLines(
        stripAbLine as any,
        offsets,
        fieldGeometry as any
      );
      const { strips } = calculateStripAreas(lines, fieldGeometry as any);
      if (strips.length > 0) {
        return strips.map((strip, i) =>
          makeFeature(strip.geometry, strip.index ?? i)
        );
      }
    }
  } catch (error) {
    console.warn("Strip cell recompute failed; using saved geometry:", error);
  }

  // Fallback: use the saved geometry directly.
  const geom = trialItem?.plot?.geojson?.geometry;

  // Preferred saved shape: a GeometryCollection with one cell (Polygon or
  // MultiPolygon) per strip — render each member as a cell.
  if (geom?.type === "GeometryCollection" && Array.isArray(geom.geometries)) {
    return geom.geometries.map((cellGeom: any, stripIndex: number) =>
      makeFeature(cellGeom, stripIndex)
    );
  }

  // Legacy saved shape: a single flat MultiPolygon (one polygon per strip).
  const geomType = geom?.type;
  if (geomType !== "Polygon" && geomType !== "MultiPolygon") return [];
  const polygons: any[] =
    geomType === "Polygon" ? [geom.coordinates] : geom.coordinates;
  return polygons.map((polygonCoords: any, stripIndex: number) =>
    makeFeature({ type: "Polygon", coordinates: polygonCoords }, stripIndex)
  );
};

interface RenderPlotGridLayersParams {
  map: mapboxgl.Map;
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>;
  gridSourceId: string;
  gridLayerId: string;
  transParentLayerId: string;
  trialPlotId: number;
  visible: boolean;
  id: number;
  isError: boolean;
}

/** Draws/updates a plot's grid source, fill/border layers, and per-cell HTML labels. */
function renderPlotGridLayers({
  map,
  gridGeoJSON,
  gridSourceId,
  gridLayerId,
  transParentLayerId,
  trialPlotId,
  visible,
  id,
  isError,
}: RenderPlotGridLayersParams): void {
  if (!map.getSource(gridSourceId)) {
    map.addSource(gridSourceId, {
      type: "geojson",
      data: gridGeoJSON,
    });

    map.addLayer({
      id: gridLayerId,
      type: "fill",
      source: gridSourceId,
      paint: {
        "fill-color": RGBA_PLOT_FILL,
      },
    });

    map.addLayer({
      id: transParentLayerId,
      type: "line",
      source: gridSourceId,
      paint: {
        "line-color": RGBA_TRIAL_PLOT,
        "line-width": 2,
      },
    });
  } else {
    (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
      gridGeoJSON
    );
  }

  setLayerVisibilityOfTrialPlots(
    map,
    gridLayerId,
    visible,
    id,
    gridGeoJSON,
    gridSourceId,
    transParentLayerId
  );

  gridGeoJSON.features.forEach((feature, index) => {
    const cellLayerId = `${trialPlotId}-${index}`;

    if (map.getLayer(cellLayerId)) {
      map.removeLayer(cellLayerId);
    }

    map.addLayer({
      id: cellLayerId,
      type: "fill",
      source: gridSourceId,
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 1,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });

    const cellBorderLayerId = `${cellLayerId}-border`;
    if (map.getLayer(cellBorderLayerId)) {
      map.removeLayer(cellBorderLayerId);
    }

    map.addLayer({
      id: cellBorderLayerId,
      type: "line",
      source: gridSourceId,
      paint: {
        "line-color": isError ? RGBA_TRIAL_PLOT_RED : RGBA_TRANSPARENT_COLOR,
        "line-width": 2,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });
  });

  // Remove ALL existing label markers for this plot first. When the grid shrinks
  // (e.g. 3×3 → 2×2) the loop below only re-adds the remaining cells, so markers
  // for the removed cells (labels 5..9) would otherwise linger at their old spots
  // (they are HTML markers positioned by lng/lat, so replacing the source data
  // does not clear them).
  Object.keys(markersForTrialPlot)
    .filter((markerId) =>
      markerId.startsWith(`plot-label-marker-${trialPlotId}-`)
    )
    .forEach((markerId) => {
      markersForTrialPlot[markerId].remove();
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete markersForTrialPlot[markerId];
    });

  // Add one HTML marker label per plot cell, centred on the cell.
  gridGeoJSON.features.forEach((feature, index) => {
    const center = turf.centerOfMass(feature).geometry.coordinates as [
      number,
      number
    ];
    createPlotLabelMarker(map, center, `${index + 1}`, trialPlotId, index);
  });
}

interface RenderPlotGroupLabelParams {
  map: mapboxgl.Map;
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>;
  gridSourceId: string;
  id: number;
}

/** Draws (or redraws) a plot's group label (A, B, C, ...) above its grid. */
function renderPlotGroupLabel({
  map,
  gridGeoJSON,
  gridSourceId,
  id,
}: RenderPlotGroupLabelParams): void {
  // Convert id to A, B, C, ... (ASCII 65 is 'A')
  const groupLabel = String.fromCodePoint(65 + (id % 26));
  const groupLabelSourceId = `${gridSourceId}-group-label`;
  const groupLabelLayerId = `${groupLabelSourceId}-label-group`;

  // Remove existing group label if it exists
  if (map.getLayer(groupLabelLayerId)) {
    map.removeLayer(groupLabelLayerId);
  }
  if (map.getSource(groupLabelSourceId)) {
    map.removeSource(groupLabelSourceId);
  }

  // Get the bounds of the grid to position the label outside
  const gridBounds = turf.bbox(gridGeoJSON);
  const gridCenter = turf.center(gridGeoJSON);

  // Create a point slightly above the grid for the label (with minimal offset)
  const labelPoint = turf.point([
    gridCenter.geometry.coordinates[0],
    gridBounds[3] + (gridBounds[3] - gridBounds[1]) * 0.01, // Very small offset above the grid
  ]);

  map.addSource(groupLabelSourceId, {
    type: "geojson",
    data: labelPoint,
  });

  // Add group label layer
  addLabelToMap(
    map,
    groupLabelSourceId,
    groupLabel, // Group label (A, B, C, ...)
    "bottom", // Anchor at the bottom of the text so it points to the plot
    [0, -0.2], // Small negative y offset to position closer to the plot
    "group",
    14 // Increased size for better visibility
  );
}

interface ResolveFieldPlacementEnvelopeParams {
  map: mapboxgl.Map;
  trialPlotId: number;
  polygonGeoJSON: PolygonGeoJSON;
  randomPointsSourceId: string;
  envelopeSourceId: string;
  trialItem: any;
}

/**
 * Resolves (and draws the debug sources for) the parcel a new/edited plot must
 * fit inside, the random-point envelope used to seed its placement search, and
 * that envelope's bbox.
 *
 * A new plot block must fit inside a single parcel. For a MultiPolygon field,
 * the random placement points are seeded into the largest sub-polygon only, so
 * the block doesn't land in the gap between parcels. A plain Polygon field
 * keeps the exact previous behaviour (placementArea === polygonGeoJSON).
 */
function resolveFieldPlacementEnvelope({
  map,
  trialPlotId,
  polygonGeoJSON,
  randomPointsSourceId,
  envelopeSourceId,
  trialItem,
}: ResolveFieldPlacementEnvelopeParams): {
  fieldPolygons: turf.Feature<turf.Polygon>[];
  placementArea: any;
  envelope: turf.Feature<turf.Polygon>;
  envelopeBBox: turf.BBox;
} {
  const containsZeroCoordinates =
    trialItem?.plot?.geojson?.geometry?.coordinates &&
    Array.isArray(trialItem.plot.geojson.geometry.coordinates) &&
    trialItem.plot.geojson.geometry.coordinates.some(
      (coord: any) => coord && coord[INDEX_ZERO] === 0 && coord[INDEX_ONE] === 0
    );

  const fieldPolygons = getSubPolygons(
    polygonGeoJSON.features?.[0]?.geometry as turf.Geometry
  );
  const placementArea: any =
    fieldPolygons.length > 1
      ? fieldPolygons.reduce(
          (best, current) =>
            turf.area(current) > turf.area(best) ? current : best,
          fieldPolygons[0]
        )
      : polygonGeoJSON;

  let pointsWithinPolygon;
  if (containsZeroCoordinates || !randomPointsCache[trialPlotId]) {
    const randomPoints = turf.randomPoint(10, {
      bbox: turf.bbox(placementArea),
    });
    pointsWithinPolygon = turf.pointsWithinPolygon(randomPoints, placementArea);
    randomPointsCache[trialPlotId] = pointsWithinPolygon;
  } else {
    pointsWithinPolygon = randomPointsCache[trialPlotId];
  }

  // Sliver / concave parcels can leave `pointsWithinPolygon` empty (all random
  // bbox points miss the polygon). `turf.envelope` of an empty collection is
  // degenerate, which would place the grid off the field. Fall back to a
  // guaranteed-interior point so the starting point is always meaningful.
  if (!pointsWithinPolygon?.features?.length) {
    pointsWithinPolygon = turf.featureCollection([
      turf.pointOnFeature(placementArea),
    ]);
    randomPointsCache[trialPlotId] = pointsWithinPolygon;
  }
  const envelope = turf.envelope(pointsWithinPolygon);

  if (!map.getSource(randomPointsSourceId)) {
    map.addSource(randomPointsSourceId, {
      type: "geojson",
      data: pointsWithinPolygon as GeoJSON.FeatureCollection,
    });
  } else {
    (map.getSource(randomPointsSourceId) as mapboxgl.GeoJSONSource).setData(
      pointsWithinPolygon as GeoJSON.FeatureCollection
    );
  }

  if (!map.getSource(envelopeSourceId)) {
    map.addSource(envelopeSourceId, {
      type: "geojson",
      data: envelope,
    });
  } else {
    (map.getSource(envelopeSourceId) as mapboxgl.GeoJSONSource).setData(
      envelope
    );
  }

  const envelopeBBox = turf.bbox(envelope);

  return { fieldPolygons, placementArea, envelope, envelopeBBox };
}

interface RenderStripPlotCellsParams {
  map: mapboxgl.Map;
  trialItem: any;
  trialPlotId: number;
  plotGeoJson: any;
  polygonGeoJSON: PolygonGeoJSON;
  newTrialState: NewTrialStateInterface;
  gridSourceId: string;
  visible: boolean;
}

/**
 * Renders ONE cell per strip (each a Polygon or MultiPolygon — mapbox fills
 * all subparts) with the same per-cell fill/border layers as a regular plot,
 * so the rates step colours each strip independently and every disjoint
 * subpart of a strip fills. The per-strip geometries are recomputed from the
 * saved offsets, so we always get exactly N strips (not one cell per
 * flattened polygon).
 *
 * Returns true when trialItem was a strip plot (handled here — the caller
 * must return without creating drag/rotate markers), false otherwise.
 */
function renderStripPlotCells({
  map,
  trialItem,
  trialPlotId,
  plotGeoJson,
  polygonGeoJSON,
  newTrialState,
  gridSourceId,
  visible,
}: RenderStripPlotCellsParams): boolean {
  if (!trialItem?.plot?.isStripPlot) return false;

  try {
    // A strip plot must NEVER show the regular plot's per-sub-polygon labels
    // (it creates one "1,2,3,..." HTML marker per polygon, so a 3-strip group
    // flattened to 5 polygons shows 1..5). Remove any that linger from a prior
    // render — strip numbers come solely from drawStripLinesToMap (per strip,
    // repeated on each disjoint piece).
    const staleLabelCount = Math.max(
      50,
      Array.isArray(plotGeoJson?.geometry?.coordinates)
        ? plotGeoJson.geometry.coordinates.length
        : 0
    );
    for (let i = 0; i < staleLabelCount; i++) {
      const staleMarkerId = `plot-label-marker-${trialPlotId}-${i}`;
      if (markersForTrialPlot[staleMarkerId]) {
        markersForTrialPlot[staleMarkerId].remove();
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete markersForTrialPlot[staleMarkerId];
      }
    }

    const stripFeatures = buildStripCellFeatures(
      trialItem,
      trialPlotId,
      polygonGeoJSON,
      newTrialState
    );

    if (stripFeatures.length > 0) {
      const stripGeoJSON = turf.featureCollection(stripFeatures);

      if (!map.getSource(gridSourceId)) {
        map.addSource(gridSourceId, {
          type: "geojson",
          data: stripGeoJSON as GeoJSON.FeatureCollection,
        });
      } else {
        (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
          stripGeoJSON as GeoJSON.FeatureCollection
        );
      }

      // Insert the rate-coloured cell fills BELOW the strip number labels
      // (drawn by drawStripLinesToMap) so the labels always stay on top. This
      // loop runs after the strip lines are drawn, so without a beforeId the
      // fills would cover the "1,2,3" labels. If no label layer is on the map
      // yet, undefined keeps the previous "add on top" behaviour.
      const stripLabelBeforeId = (map.getStyle()?.layers ?? [])
        .map((styleLayer) => styleLayer.id)
        .find((styleLayerId) => styleLayerId.endsWith("-strip-area-labels"));
      const labelBeforeId =
        stripLabelBeforeId && map.getLayer(stripLabelBeforeId)
          ? stripLabelBeforeId
          : undefined;

      // Per-strip fill + border layers, mirroring the regular-plot cells.
      stripFeatures.forEach((feature, stripIndex) => {
        const cellLayerId = `${trialPlotId}-${stripIndex}`;
        if (map.getLayer(cellLayerId)) map.removeLayer(cellLayerId);
        map.addLayer(
          {
            id: cellLayerId,
            type: "fill",
            source: gridSourceId,
            paint: {
              "fill-color": ["get", "color"],
              "fill-opacity": 1,
            },
            layout: { visibility: visible ? "visible" : "none" },
            filter: ["==", ["get", "id"], feature.properties.id],
          },
          labelBeforeId
        );

        const cellBorderLayerId = `${cellLayerId}-border`;
        if (map.getLayer(cellBorderLayerId)) {
          map.removeLayer(cellBorderLayerId);
        }
        map.addLayer(
          {
            id: cellBorderLayerId,
            type: "line",
            source: gridSourceId,
            paint: { "line-color": "#000000", "line-width": 1 },
            layout: { visibility: visible ? "visible" : "none" },
            filter: ["==", ["get", "id"], feature.properties.id],
          },
          labelBeforeId
        );
      });
    }

    // Strip plots are exempt from field-boundary validation and are never
    // draggable — always return so no move/rotate markers are created.
    // NOTE: strip number labels are rendered once by drawStripLinesToMap
    // (showLabels), so both the Strip Design and Rates screens show the same
    // labels — we do not create separate labels here.
    return true;
  } catch (error) {
    console.error("❌ Error creating strip plot:", error);
    return true; // Never fall through to the draggable-plot path for strip plots.
  }
}

interface ComputePlotGridForRenderParams {
  trialPlotId: number;
  rows: number;
  columns: number;
  plotWidth: number;
  plotHeight: number;
  columnSize: number;
  rowSize: number;
  plotGeoJson: any;
  newTrialState: NewTrialStateInterface;
  trialItem: any;
  fieldPolygons: turf.Feature<turf.Polygon>[];
  placementArea: any;
  envelopeBBox: turf.BBox;
}

/**
 * Computes this render's plot grid (cells + rotation-marker origin), and
 * mutates trialItem.plot.geojson.geometry.coordinates to match.
 *
 * New/editing plots search for an AB-line-anchored, in-field, non-overlapping
 * placement (or the best-effort/edit-preserving fallback); anything else
 * rebuilds the grid directly from its already-saved MultiPolygon coordinates.
 */
interface AbLineGridInfo {
  abBearing: number | null;
  abFirstCoord: [number, number] | null;
  abGridWidth: number;
}

/** Resolves the AB-line bearing/origin/grid-width used to anchor new plots. */
function resolveAbLineGridInfo(
  newTrialState: NewTrialStateInterface
): AbLineGridInfo {
  const firstAbGrid = newTrialState.ab_line?.[0];
  let abBearing: number | null = null;
  let abFirstCoord: [number, number] | null = null;
  let abGridWidth = 50;
  if (
    newTrialState.ab_line.length > 0 &&
    Array.isArray(firstAbGrid?.geojson?.geometry?.coordinates) &&
    firstAbGrid.geojson.geometry.coordinates.length >= 2 &&
    firstAbGrid.geojson.geometry.coordinates[0] &&
    firstAbGrid.geojson.geometry.coordinates[1]
  ) {
    try {
      const abCoords = firstAbGrid.geojson.geometry.coordinates;
      abBearing = turf.bearing(
        turf.point(abCoords[0]),
        turf.point(abCoords[1])
      );
      abFirstCoord = abCoords[0] as [number, number];
      abGridWidth = firstAbGrid.grid_width ?? 50;
    } catch {
      abBearing = null;
    }
  }
  return { abBearing, abFirstCoord, abGridWidth };
}

interface GridPlacementContext {
  trialPlotId: number;
  rows: number;
  columns: number;
  width: number;
  height: number;
  abBearing: number | null;
  abFirstCoord: [number, number] | null;
  abGridWidth: number;
}

/**
 * Builds a plot block centred on `centrePoint`, rotated to the AB bearing and
 * snapped to the machinery grid. `snapMode` "abOnly" pins the leading edge to
 * the AB line (used for the very first plot); "grid" snaps to the nearest
 * grid line so later plots can occupy any free row of the machinery grid;
 * "none" rotates to the AB bearing but does NOT snap, so the block stays
 * centred exactly on `centrePoint` (used when editing keeps the plot where it
 * is — snapping would shift a resized grid off its centre).
 */
function buildPlacedGridAt(
  centrePoint: [number, number],
  snapMode: "abOnly" | "grid" | "none",
  ctx: GridPlacementContext
): turf.FeatureCollection<turf.Polygon> {
  const {
    trialPlotId,
    rows,
    columns,
    width,
    height,
    abBearing,
    abFirstCoord,
    abGridWidth,
  } = ctx;

  // Offset the corner so the block is centred on `centrePoint` (the grid
  // grows east/south from its corner).
  const corner = getRhumbOffsetPoint(
    centrePoint,
    -(columns * width) / 2,
    (rows * height) / 2
  );
  const axisAlignedGrid = buildAxisAlignedPlotGrid(
    corner,
    rows,
    columns,
    width,
    height
  );
  let placed = turf.featureCollection(
    axisAlignedGrid.features.map((feature, index) => {
      const rowIndex = Math.floor(index / columns);
      const columnIndex = index % columns;
      return {
        ...feature,
        properties: {
          id: `${trialPlotId}-${rowIndex}-${columnIndex}`,
          color: RGBA_PLOT_FILL,
        },
      };
    })
  ) as turf.FeatureCollection<turf.Polygon>;

  if (abBearing !== null && abFirstCoord) {
    const centre = turf.center(placed).geometry.coordinates;
    placed = turf.transformRotate(placed, abBearing, {
      pivot: centre,
    }) as turf.FeatureCollection<turf.Polygon>;
    if (snapMode !== "none") {
      try {
        placed = snapGridToAbLine(
          placed,
          abFirstCoord,
          abBearing,
          abGridWidth,
          {
            mode: snapMode === "abOnly" ? "abOnly" : "nearestGrid",
          }
        );
      } catch (snapError) {
        console.warn(
          "⚠️ Grid-line snapping failed, using unsnapped position:",
          snapError
        );
      }
    }
  }
  return placed;
}

interface OldPlotGeometry {
  editCentre: [number, number] | null;
  originCorner: [number, number] | null;
  oldVertices: number[][];
}

/** Extracts an editing plot's current centre, NW origin corner and vertices. */
function extractOldPlotGeometry(plotGeoJson: any): OldPlotGeometry {
  let editCentre: [number, number] | null = null;
  let originCorner: [number, number] | null = null;
  const oldVertices: number[][] = [];
  try {
    const oldCoords = plotGeoJson?.geometry?.coordinates;
    if (Array.isArray(oldCoords) && Array.isArray(oldCoords[0]?.[0])) {
      const cells = (oldCoords as any[])
        .map((polyCoords) => {
          try {
            return turf.polygon(polyCoords);
          } catch {
            return null;
          }
        })
        .filter((cell): cell is turf.Feature<turf.Polygon> => cell !== null);
      if (cells.length > 0) {
        editCentre = turf.center(turf.featureCollection(cells)).geometry
          .coordinates as [number, number];
        cells.forEach((cell) =>
          turf.coordAll(cell).forEach((coord) => oldVertices.push(coord))
        );
        // Origin (NW) corner: the first cell's NW vertex — the same reference
        // getNorthWestCorner / the rotate handle uses.
        originCorner = oldCoords[0]?.[0]?.[1] as [number, number];
      }
    }
  } catch {
    editCentre = null;
  }
  return { editCentre, originCorner, oldVertices };
}

/** Field centroid to slide an editing plot toward when it doesn't fit. */
function resolveFieldCentreForEdit(
  fieldPolygons: turf.Feature<turf.Polygon>[],
  fallback: [number, number]
): [number, number] {
  try {
    return turf.centroid(turf.featureCollection(fieldPolygons)).geometry
      .coordinates as [number, number];
  } catch {
    return fallback;
  }
}

/**
 * Footprint half-extents (metres) along the AB axes (u = along the AB line,
 * v = perpendicular), measured from `origin`.
 */
function computeHalfExtentsAlongAxes(
  vertices: number[][],
  origin: [number, number],
  bearing: number
): { uHalf: number; vHalf: number } {
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  const originPoint = turf.point(origin);
  vertices.forEach((vertex) => {
    const point = turf.point(vertex);
    const dist = turf.distance(originPoint, point, { units: "meters" });
    const theta =
      ((turf.bearing(originPoint, point) - bearing) * Math.PI) / 180;
    const u = dist * Math.cos(theta);
    const v = dist * Math.sin(theta);
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  });
  return { uHalf: (uMax - uMin) / 2, vHalf: (vMax - vMin) / 2 };
}

/** Translates `grid` by `uMeters`/`vMeters` along/perpendicular to `bearing`. */
function translateGridAlongAxes(
  grid: turf.FeatureCollection<turf.Polygon>,
  bearing: number,
  uMeters: number,
  vMeters: number
): turf.FeatureCollection<turf.Polygon> {
  let out = grid;
  if (Math.abs(uMeters) > 1e-6) {
    out = turf.transformTranslate(
      out,
      Math.abs(uMeters) / 1000,
      uMeters > 0 ? bearing : bearing + 180,
      { units: "kilometers" }
    ) as turf.FeatureCollection<turf.Polygon>;
  }
  if (Math.abs(vMeters) > 1e-6) {
    out = turf.transformTranslate(
      out,
      Math.abs(vMeters) / 1000,
      vMeters > 0 ? bearing + 90 : bearing - 90,
      { units: "kilometers" }
    ) as turf.FeatureCollection<turf.Polygon>;
  }
  return out;
}

/**
 * Tries interpolation fractions 0.1..1.0 of the way from the current
 * placement toward the field centre, returning the first candidate that
 * fits inside the field (or null if none does).
 */
function slideTowardCentreUntilFits(
  fitsField: (grid: turf.FeatureCollection<turf.Polygon>) => boolean,
  buildCandidateAt: (t: number) => turf.FeatureCollection<turf.Polygon> | null
): turf.FeatureCollection<turf.Polygon> | null {
  for (let t = 0.1; t <= 1.00001; t += 0.1) {
    const candidate = buildCandidateAt(t);
    if (candidate && fitsField(candidate)) {
      return candidate;
    }
  }
  return null;
}

interface ResizeEditingPlotAlongAbLineParams {
  rows: number;
  columns: number;
  editCentre: [number, number];
  originCorner: [number, number];
  oldVertices: number[][];
  bearing: number;
  fieldCentre: [number, number];
  gridCtx: GridPlacementContext;
  fitsField: (grid: turf.FeatureCollection<turf.Polygon>) => boolean;
}

/**
 * Resizes an editing plot while keeping it grid-aligned: pins the NW origin
 * corner first (natural direction); if the resized grid spills out of the
 * field, tries pinning each of the other corners instead; only if none of
 * those fit does it slide the NW-anchored grid the minimum needed toward the
 * field centre.
 */
function resizeEditingPlotAlongAbLine(
  params: ResizeEditingPlotAlongAbLineParams
): turf.FeatureCollection<turf.Polygon> | null {
  const {
    rows,
    columns,
    editCentre,
    originCorner,
    oldVertices,
    bearing,
    fieldCentre,
    gridCtx,
    fitsField,
  } = params;

  // Resized grid at the current centre, rotated to the AB bearing but NOT
  // snapped, then translated so its NW corner lands exactly on the plot's
  // current NW origin — so the origin stays put and grid-aligned.
  const resized = buildPlacedGridAt(editCentre, "none", gridCtx);
  const resizedNw = getNorthWestCorner(resized, rows, columns) as [
    number,
    number
  ];
  const alignKm = turf.distance(
    turf.point(resizedNw),
    turf.point(originCorner),
    { units: "kilometers" }
  );
  const nwAnchored =
    alignKm > 1e-9
      ? (turf.transformTranslate(
          resized,
          alignKm,
          turf.bearing(turf.point(resizedNw), turf.point(originCorner)),
          { units: "kilometers" }
        ) as turf.FeatureCollection<turf.Polygon>)
      : resized;

  // Footprint size change along the AB axes (metres), used to pin the other
  // corners by translating back.
  const oldExt = computeHalfExtentsAlongAxes(oldVertices, editCentre, bearing);
  const newExt = computeHalfExtentsAlongAxes(
    turf.coordAll(nwAnchored),
    editCentre,
    bearing
  );
  const du = (newExt.uHalf - oldExt.uHalf) * 2; // width change along u
  const dv = (newExt.vHalf - oldExt.vHalf) * 2; // height change along v

  // Natural direction (NW origin fixed) first; if it spills, pin each of the
  // other corners by translating the NW-anchored grid back by the size change.
  const candidates: Array<turf.FeatureCollection<turf.Polygon>> = [
    nwAnchored,
    translateGridAlongAxes(nwAnchored, bearing, -du, 0), // keep NE
    translateGridAlongAxes(nwAnchored, bearing, 0, -dv), // keep SW
    translateGridAlongAxes(nwAnchored, bearing, -du, -dv), // keep SE
  ];
  for (const candidate of candidates) {
    if (fitsField(candidate)) {
      return candidate;
    }
  }

  // Last resort: slide the NW-anchored grid toward the field centre.
  const toFieldKm = turf.distance(
    turf.point(editCentre),
    turf.point(fieldCentre),
    { units: "kilometers" }
  );
  if (toFieldKm <= 1e-9) {
    return null;
  }
  const dirBearing = turf.bearing(
    turf.point(editCentre),
    turf.point(fieldCentre)
  );
  return slideTowardCentreUntilFits(
    fitsField,
    (t) =>
      turf.transformTranslate(nwAnchored, toFieldKm * t, dirBearing, {
        units: "kilometers",
      }) as turf.FeatureCollection<turf.Polygon>
  );
}

interface ResizeEditingPlotWithoutAbLineParams {
  editCentre: [number, number];
  fieldCentre: [number, number];
  gridCtx: GridPlacementContext;
  fitsField: (grid: turf.FeatureCollection<turf.Polygon>) => boolean;
}

/**
 * No AB line (no machinery grid): keeps the plot where it is, unsnapped,
 * then slides toward the field centre if it doesn't fit.
 */
function resizeEditingPlotWithoutAbLine(
  params: ResizeEditingPlotWithoutAbLineParams
): turf.FeatureCollection<turf.Polygon> | null {
  const { editCentre, fieldCentre, gridCtx, fitsField } = params;
  try {
    const candidate = buildPlacedGridAt(editCentre, "none", gridCtx);
    if (fitsField(candidate)) {
      return candidate;
    }
  } catch {
    /* fall through to the slide */
  }
  return slideTowardCentreUntilFits(fitsField, (t) => {
    try {
      const centre: [number, number] = [
        editCentre[0] + (fieldCentre[0] - editCentre[0]) * t,
        editCentre[1] + (fieldCentre[1] - editCentre[1]) * t,
      ];
      return buildPlacedGridAt(centre, "none", gridCtx);
    } catch {
      return null;
    }
  });
}

interface ResolvePlacementForEditingPlotParams {
  rows: number;
  columns: number;
  plotGeoJson: any;
  fieldPolygons: turf.Feature<turf.Polygon>[];
  abBearing: number | null;
  gridCtx: GridPlacementContext;
}

/**
 * Editing plots keep their current position: pins the origin corner and
 * resizes along the AB grid axes when an AB line is present, otherwise keeps
 * the plot's centre unsnapped — sliding toward the field centre only if the
 * result would spill outside the field. Overlap with other plots is
 * tolerated here.
 */
function resolvePlacementForEditingPlot(
  params: ResolvePlacementForEditingPlotParams
): turf.FeatureCollection<turf.Polygon> | null {
  const { rows, columns, plotGeoJson, fieldPolygons, abBearing, gridCtx } =
    params;

  const { editCentre, originCorner, oldVertices } =
    extractOldPlotGeometry(plotGeoJson);
  if (!editCentre) {
    return null;
  }

  const fieldCentre = resolveFieldCentreForEdit(fieldPolygons, editCentre);
  const fitsField = (grid: turf.FeatureCollection<turf.Polygon>): boolean =>
    isGridInsideSingleParcel(grid, fieldPolygons);

  if (abBearing !== null && originCorner) {
    return resizeEditingPlotAlongAbLine({
      rows,
      columns,
      editCentre,
      originCorner,
      oldVertices,
      bearing: abBearing,
      fieldCentre,
      gridCtx,
      fitsField,
    });
  }
  return resizeEditingPlotWithoutAbLine({
    editCentre,
    fieldCentre,
    gridCtx,
    fitsField,
  });
}

interface SearchPlacementForNewPlotParams {
  candidateCentres: Array<[number, number]>;
  primarySnap: "abOnly" | "grid";
  gridCtx: GridPlacementContext;
  fieldPolygons: turf.Feature<turf.Polygon>[];
  existingFootprints: turf.Feature<turf.Polygon>[];
}

/**
 * Searches for a placement that is fully inside a single parcel AND does not
 * overlap any existing plot, tracking a best-effort candidate as a fallback.
 */
function searchPlacementForNewPlot(params: SearchPlacementForNewPlotParams): {
  chosenGrid: turf.FeatureCollection<turf.Polygon> | null;
  bestEffortGrid: turf.FeatureCollection<turf.Polygon> | null;
} {
  const {
    candidateCentres,
    primarySnap,
    gridCtx,
    fieldPolygons,
    existingFootprints,
  } = params;

  let bestEffortGrid: turf.FeatureCollection<turf.Polygon> | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const centre of candidateCentres) {
    let candidate: turf.FeatureCollection<turf.Polygon>;
    try {
      candidate = buildPlacedGridAt(centre, primarySnap, gridCtx);
    } catch {
      continue;
    }
    const inside = isGridInsideSingleParcel(candidate, fieldPolygons);
    const overlaps = gridOverlapsFootprints(candidate, existingFootprints);
    if (inside && !overlaps) {
      return { chosenGrid: candidate, bestEffortGrid };
    }
    const score =
      countCellsInsideField(candidate, fieldPolygons) -
      (overlaps ? candidate.features.length : 0);
    if (score > bestScore) {
      bestScore = score;
      bestEffortGrid = candidate;
    }
  }
  return { chosenGrid: null, bestEffortGrid };
}

/**
 * First plot: if pinning to the AB line left it outside the field, retries
 * allowing any grid line so it can sit fully inside.
 */
function retryFirstPlotAnyGridLine(
  candidateCentres: Array<[number, number]>,
  gridCtx: GridPlacementContext,
  fieldPolygons: turf.Feature<turf.Polygon>[]
): turf.FeatureCollection<turf.Polygon> | null {
  for (const centre of candidateCentres) {
    let candidate: turf.FeatureCollection<turf.Polygon>;
    try {
      candidate = buildPlacedGridAt(centre, "grid", gridCtx);
    } catch {
      continue;
    }
    if (isGridInsideSingleParcel(candidate, fieldPolygons)) {
      return candidate;
    }
  }
  return null;
}

/**
 * Computes the plot grid for a new or currently-editing plot: searches for
 * an AB-line-anchored, in-field, non-overlapping placement (or the
 * best-effort/edit-preserving fallback).
 */
function computeNewOrEditingPlotGrid(params: ComputePlotGridForRenderParams): {
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>;
  initialRotationMarkerPosition: number[];
} {
  const {
    trialPlotId,
    rows,
    columns,
    plotWidth,
    plotHeight,
    columnSize,
    rowSize,
    plotGeoJson,
    newTrialState,
    trialItem,
    fieldPolygons,
    placementArea,
    envelopeBBox,
  } = params;

  const width = columnSize ?? plotWidth;
  const height = rowSize ?? plotHeight;

  // AB-line info: new plots are rotated to the AB bearing and snapped to the
  // machinery grid so they tile with it.
  const { abBearing, abFirstCoord, abGridWidth } =
    resolveAbLineGridInfo(newTrialState);
  const gridCtx: GridPlacementContext = {
    trialPlotId,
    rows,
    columns,
    width,
    height,
    abBearing,
    abFirstCoord,
    abGridWidth,
  };

  // Footprints of already-placed plots so the new block doesn't land on top of
  // an existing one.
  const existingFootprints = collectExistingPlotFootprints(
    newTrialState,
    trialPlotId
  );
  const isFirstPlot = existingFootprints.length === 0;

  // Fallback centre = the previous envelope-bbox centre (kept for parity).
  const [eMinX, eMinY, eMaxX, eMaxY] = envelopeBBox;
  const fallbackCentre: [number, number] = [
    (eMinX + eMaxX) / 2,
    (eMinY + eMaxY) / 2,
  ];

  // Candidate centres spread across the parcel (variety so plots don't stack)
  // plus the fallback centre.
  const candidateCentres: Array<[number, number]> = [
    ...generatePlacementCandidatePoints(placementArea, 60),
    fallbackCentre,
  ];

  const primarySnap: "abOnly" | "grid" = isFirstPlot ? "abOnly" : "grid";

  // EDITING: keep the plot exactly where it is (see resolvePlacementForEditingPlot).
  let chosenGrid = trialItem.plot.isEditing
    ? resolvePlacementForEditingPlot({
        rows,
        columns,
        plotGeoJson,
        fieldPolygons,
        abBearing,
        gridCtx,
      })
    : null;

  // Search for a non-editing (or edit-unresolved) placement.
  // (Skipped when editing already kept/relocated the plot above.)
  let bestEffortGrid: turf.FeatureCollection<turf.Polygon> | null = null;
  if (!chosenGrid) {
    const searchResult = searchPlacementForNewPlot({
      candidateCentres,
      primarySnap,
      gridCtx,
      fieldPolygons,
      existingFootprints,
    });
    chosenGrid = searchResult.chosenGrid;
    bestEffortGrid = searchResult.bestEffortGrid;
  }

  // First plot: if pinning to the AB line left it outside the field, retry
  // allowing any grid line so it can sit fully inside.
  if (!chosenGrid && isFirstPlot) {
    chosenGrid = retryFirstPlotAnyGridLine(
      candidateCentres,
      gridCtx,
      fieldPolygons
    );
  }

  const gridGeoJSON =
    chosenGrid ??
    bestEffortGrid ??
    buildPlacedGridAt(fallbackCentre, primarySnap, gridCtx);

  const initialRotationMarkerPosition = getNorthWestCorner(
    gridGeoJSON,
    rows,
    columns
  );

  return { gridGeoJSON, initialRotationMarkerPosition };
}

/**
 * Rebuilds the plot grid directly from its already-saved MultiPolygon
 * coordinates (used for plots that are neither new nor being edited).
 */
function buildGridFromSavedCoordinates(
  params: ComputePlotGridForRenderParams
): {
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>;
  initialRotationMarkerPosition: number[];
} {
  const { trialPlotId, rows, columns, plotGeoJson } = params;

  let propertiesArray: any[] = [];
  for (let i = INDEX_ZERO; i < rows; i++) {
    for (let j = INDEX_ZERO; j < columns; j++) {
      const color = RGBA_PLOT_FILL;
      const properties = { id: `${trialPlotId}-${i}-${j}`, color };
      propertiesArray.push(properties);
    }
  }

  const gridFeatures: Array<turf.Feature<turf.Polygon>> = [];
  plotGeoJson?.geometry?.coordinates?.forEach(
    (coordinateSet: any, index: number) => {
      const feature: any = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: coordinateSet,
        },
        properties: propertiesArray[index] || {},
      };
      gridFeatures.push(feature);
    }
  );

  // Calculate bearing safely based on geometry type
  let bearing = 0;
  try {
    if (
      plotGeoJson.geometry.type === "Polygon" &&
      plotGeoJson.geometry.coordinates[0]?.length >= 2
    ) {
      // For polygon geometry (strip plots), use first two points of the outer ring
      const coords = plotGeoJson.geometry.coordinates[0];
      bearing = turf.bearing(turf.point(coords[0]), turf.point(coords[1]));
    } else if (plotGeoJson.geometry.coordinates?.length >= 2) {
      // For other geometry types, use the original logic
      bearing = turf.bearing(
        turf.point(plotGeoJson.geometry.coordinates[0][0][0]),
        turf.point(plotGeoJson.geometry.coordinates[0][0][1])
      );
    }
  } catch (error) {
    console.warn("⚠️ Error calculating bearing, using default 0:", error);
    bearing = 0;
  }

  const gridGeoJSON = turf.featureCollection(gridFeatures);
  const initialRotationMarkerPosition = getNorthWestCorner(
    gridGeoJSON,
    rows,
    columns
  );

  return { gridGeoJSON, initialRotationMarkerPosition };
}

/**
 * Computes this render's plot grid (cells + rotation-marker origin), and
 * mutates trialItem.plot.geojson.geometry.coordinates to match.
 *
 * New/editing plots search for an AB-line-anchored, in-field, non-overlapping
 * placement (or the best-effort/edit-preserving fallback); anything else
 * rebuilds the grid directly from its already-saved MultiPolygon coordinates.
 */
function computePlotGridForRender(params: ComputePlotGridForRenderParams): {
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>;
  initialRotationMarkerPosition: number[];
} {
  const { plotGeoJson, trialItem } = params;

  const needsPlacement =
    plotGeoJson?.geometry?.coordinates?.[0] === 0 ||
    plotGeoJson?.geometry?.coordinates?.[1] === 0 ||
    trialItem.plot.isEditing;

  const { gridGeoJSON, initialRotationMarkerPosition } = needsPlacement
    ? computeNewOrEditingPlotGrid(params)
    : buildGridFromSavedCoordinates(params);

  trialItem.plot.geojson.geometry.coordinates = gridGeoJSON?.features.map(
    (feature) => feature?.geometry?.coordinates
  );

  return { gridGeoJSON, initialRotationMarkerPosition };
}

interface CreateNewTrialPlotsParams {
  map: mapboxgl.Map;
  trialPlotId: number;
  polygonGeoJSON: PolygonGeoJSON;
  randomPointsSourceId: string;
  envelopeSourceId: string;
  gridSourceId: string;
  gridLayerId: string;
  rows: number;
  columns: number;
  plotWidth: number;
  plotHeight: number;
  columnSize: number;
  rowSize: number;
  plotGeoJson: any;
  newTrialState: NewTrialStateInterface;
  createCustomMarkerElement: (icon: string) => HTMLElement;
  visible: boolean;
  id: number;
  transParentLayerId: string;
  trialItem: any;
  plotsValidationData: PlotsValidationData;
  setPlotsValidationData: any;
  checkPlotBoundaries: any;
}

const createNewTrialPlots = ({
  map,
  trialPlotId,
  polygonGeoJSON,
  randomPointsSourceId,
  envelopeSourceId,
  gridSourceId,
  gridLayerId,
  rows,
  columns,
  plotWidth,
  plotHeight,
  columnSize,
  rowSize,
  plotGeoJson,
  newTrialState,
  createCustomMarkerElement,
  visible,
  id,
  transParentLayerId,
  trialItem,
  plotsValidationData,
  setPlotsValidationData,
  checkPlotBoundaries,
}: CreateNewTrialPlotsParams): void => {
  const envelopeCenterMarkerId = `envelope-center-marker-${trialPlotId}`;
  const rotationMarkerId = `rotation-marker-${trialPlotId}`;
  let rotationAngle = gridRotations[trialPlotId] || 0;

  // Clear ALL of this plot's markers (cell labels + move/rotate handles) up front
  // so none survive at a stale position after a resize, drag, rotate or any other
  // re-render — regardless of the previous cell count or which handles existed.
  removeAllMarkersForPlot(trialPlotId);

  // Handle strip plots differently. Strip plots are never moved/rotated →
  // ALWAYS return here (no drag markers) when this was a strip plot.
  if (
    renderStripPlotCells({
      map,
      trialItem,
      trialPlotId,
      plotGeoJson,
      polygonGeoJSON,
      newTrialState,
      gridSourceId,
      visible,
    })
  ) {
    return;
  }

  const { fieldPolygons, placementArea, envelope, envelopeBBox } =
    resolveFieldPlacementEnvelope({
      map,
      trialPlotId,
      polygonGeoJSON,
      randomPointsSourceId,
      envelopeSourceId,
      trialItem,
    });

  const plotGridResult = computePlotGridForRender({
    trialPlotId,
    rows,
    columns,
    plotWidth,
    plotHeight,
    columnSize,
    rowSize,
    plotGeoJson,
    newTrialState,
    trialItem,
    fieldPolygons,
    placementArea,
    envelopeBBox,
  });
  let gridGeoJSON = plotGridResult.gridGeoJSON;
  let initialRotationMarkerPosition =
    plotGridResult.initialRotationMarkerPosition;

  let envelopeCenter: [number, number];

  try {
    envelopeCenter = turf.center(gridGeoJSON).geometry.coordinates as [
      number,
      number
    ];
  } catch (error) {
    console.warn("⚠️ Error calculating envelope center, using default:", error);
    envelopeCenter = [0, 0]; // Default fallback coordinates
  }

  const updateRotationMarkerPosition = (data?: any): void => {
    initialRotationMarkerPosition = getRotated(
      gridGeoJSON,
      rotationAngle,
      rows,
      columns
    );

    rotationMarker.setLngLat(initialRotationMarkerPosition as [number, number]);
  };

  const envelopeCenterMarker = markerForTrialPlots(
    map,
    envelopeCenter as mapboxgl.LngLatLike,
    moveIcon,
    trialPlotId,
    createCustomMarkerElement
  );

  const rotationMarker = markerForTrialPlots(
    map,
    initialRotationMarkerPosition as mapboxgl.LngLatLike,
    rotateImage,
    trialPlotId,
    createCustomMarkerElement
  );

  markersForTrialPlot[rotationMarkerId] = rotationMarker;

  rotationMarker.on("drag", () => {
    const newCenter = envelopeCenterMarker.getLngLat()?.toArray() as
      | [number, number]
      | undefined;
    const newRotationMarkerPosition = rotationMarker.getLngLat()?.toArray() as
      | [number, number]
      | undefined;

    if (newCenter && newRotationMarkerPosition) {
      const newAngle = turf.bearing(
        turf.point(newCenter),
        turf.point(newRotationMarkerPosition)
      );
      rotationAngle = newAngle;

      const rotatedGrid = turf.transformRotate(gridGeoJSON, newAngle, {
        pivot: newCenter,
      }) as GeoJSON.FeatureCollection<
        turf.helpers.Polygon,
        turf.helpers.Properties
      >;

      const source = map.getSource(gridSourceId) as
        | mapboxgl.GeoJSONSource
        | undefined;
      if (source) {
        source.setData(rotatedGrid);
      }

      // grid rotations code removed
      updateRotationMarkerPosition(rotatedGrid);

      // Update the group label position and plot label markers when rotating
      updateGroupLabelPosition({
        map,
        baseSourceId: gridSourceId,
        rotatedGrid,
        newTrialState,
        trialPlotId,
        labelSuffix: "",
        offset: [0, -0.2],
        fontSize: 14,
      });
      updatePlotLabelMarkersPosition(rotatedGrid, trialPlotId);

      trialItem.plot.geojson.geometry.coordinates = rotatedGrid?.features.map(
        (feature) => feature?.geometry?.coordinates
      );
    }
  });
  rotationMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    // Report alignment status after user finishes rotating
    checkAndReportAlignment();
  });

  markersForTrialPlot[envelopeCenterMarkerId] = envelopeCenterMarker;
  envelopeCenterMarker.on("drag", () => {
    const newCenter = envelopeCenterMarker.getLngLat().toArray();
    const offset = [
      newCenter[INDEX_ZERO] - envelopeCenter[INDEX_ZERO],
      newCenter[INDEX_ONE] - envelopeCenter[INDEX_ONE],
    ];

    const newGridFeatures = gridGeoJSON.features.map((feature) => {
      const coords = feature.geometry.coordinates[INDEX_ZERO].map((point) => [
        point[INDEX_ZERO] + offset[INDEX_ZERO],
        point[INDEX_ONE] + offset[INDEX_ONE],
      ]);
      return turf.polygon([coords], feature.properties);
    });

    gridGeoJSON = turf.featureCollection(newGridFeatures);

    const rotatedGrid = turf.transformRotate(gridGeoJSON, rotationAngle, {
      pivot: newCenter,
    });

    // Update main grid source
    (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
      rotatedGrid
    );

    const newEnvelope = turf.transformTranslate(
      envelope,
      offset[INDEX_ZERO],
      offset[INDEX_ONE]
    );
    (map.getSource(envelopeSourceId) as mapboxgl.GeoJSONSource).setData(
      newEnvelope
    );

    initialRotationMarkerPosition = getNorthWestCorner(
      gridGeoJSON,
      rows,
      columns
    );
    rotationMarker.setLngLat(initialRotationMarkerPosition as [number, number]);
    updateRotationMarkerPosition(rotatedGrid);

    // Update the group label position and plot label markers
    updateGroupLabelPosition({
      map,
      baseSourceId: gridSourceId,
      rotatedGrid,
      newTrialState,
      trialPlotId,
      labelSuffix: "",
      offset: [0, -0.2],
      fontSize: 14,
    });
    updatePlotLabelMarkersPosition(rotatedGrid, trialPlotId);

    gridPositions[trialPlotId] = {
      lng: newCenter[INDEX_ZERO],
      lat: newCenter[INDEX_ONE],
    };
    envelopeCenter = newCenter as [number, number];
    trialItem.plot.geojson.geometry.coordinates = rotatedGrid.features.map(
      (feature) => feature.geometry.coordinates
    );
  });
  envelopeCenterMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    checkAndReportAlignment();
  });

  // Skip validation for strip plots - they should never show red borders
  const isStripPlot = trialItem?.plot?.isStripPlot;
  const isError = isStripPlot
    ? false
    : plotsValidationData?.polygonsNotInsideField?.includes(trialPlotId) ||
      plotsValidationData.intersectingPairs?.some((item: any) =>
        item.includes(trialPlotId)
      );

  renderPlotGridLayers({
    map,
    gridGeoJSON,
    gridSourceId,
    gridLayerId,
    transParentLayerId,
    trialPlotId,
    visible,
    id,
    isError,
  });

  renderPlotGroupLabel({ map, gridGeoJSON, gridSourceId, id });

  const trialPlot = newTrialState.trial_plots.find(
    (tp) => tp.plot.id === trialPlotId
  );

  if (trialPlot?.replicants) {
    trialPlot.replicants.forEach(
      (replicants: Replicants, cloneIndex: number) => {
        addReplicantToMap({
          replicants,
          cloneIndex,
          gridGeoJSON,
          trialPlotId,
          map,
          createCustomMarkerElement,
          replicantId: replicants.id as number,
          trialItem,
          plotWidth,
          plotHeight,
          rows,
          columns,
          plotsValidationData,
          setPlotsValidationData,
          checkPlotBoundaries,
          polygonGeoJSON,
          newTrialState,
        });
      }
    );
  }
};

interface AddReplicantToMapParams {
  replicants: Replicants;
  cloneIndex: number;
  gridGeoJSON: GeoJSON;
  trialPlotId: number;
  map: mapboxgl.Map;
  createCustomMarkerElement: (icon: string) => HTMLElement;
  replicantId: number;
  trialItem: any;
  plotWidth: any;
  plotHeight: any;
  rows: number;
  columns: number;
  plotsValidationData: any;
  setPlotsValidationData: any;
  checkPlotBoundaries: any;
  polygonGeoJSON: PolygonGeoJSON;
  newTrialState: NewTrialStateInterface;
}

const addReplicantToMap = ({
  replicants,
  cloneIndex,
  gridGeoJSON,
  trialPlotId,
  map,
  createCustomMarkerElement,
  replicantId,
  trialItem,
  plotWidth,
  plotHeight,
  rows,
  columns,
  plotsValidationData,
  setPlotsValidationData,
  checkPlotBoundaries,
  polygonGeoJSON,
  newTrialState,
}: AddReplicantToMapParams): void => {
  const cloneGridFeatures: GridFeature[] = [];
  const offsetDistance = 0.25 * (cloneIndex + 1);
  const offsetAngle = 120;

  const replicantGeoJson = replicants.geojson;
  if (
    replicantGeoJson.geometry.coordinates[0] === 0 &&
    replicantGeoJson.geometry.coordinates[1] === 0
  ) {
    gridGeoJSON.features.forEach((feature: any) => {
      const clonedFeature = turf.transformTranslate(
        turf.clone(feature),
        offsetDistance,
        offsetAngle
      );
      cloneGridFeatures.push(clonedFeature);
    });
  } else {
    const clonedProperties = gridGeoJSON.features.map(
      (feature) => feature.properties
    );
    replicantGeoJson.geometry.coordinates.forEach(
      (coordinateSet: any, index: number) => {
        const feature: any = {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: coordinateSet,
          },
          properties: clonedProperties[index] || {},
        };
        cloneGridFeatures.push(feature);
      }
    );
  }
  let rotationAngle = gridRotations[replicantId] ?? 0;
  let cloneGridGeoJSON: any = turf.featureCollection(cloneGridFeatures);
  const cloneGridSourceId = `cloned-grid-of-trial-plots-${cloneIndex}-${trialPlotId}-${
    replicants.id as number
  }`;
  const cloneGridLayerId = `cloned-grid-layer-${cloneIndex}-${trialPlotId}-${
    replicants.id as number
  }`;

  const envelopeCenterMarkerId = `envelope-center-marker-replicant-${
    replicants.id as number
  }`;
  const rotationMarkerId = `rotation-marker-replicant-${
    replicants.id as number
  }`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }
  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }

  let cloneEnvelopeCenter = turf.center(cloneGridGeoJSON).geometry.coordinates;
  let rotationMarkerPosition: Array<number> = getNorthWestCorner(
    cloneGridGeoJSON,
    rows,
    columns
  );

  const updateRotationMarkerPosition = (data?: any): void => {
    rotationMarkerPosition = getRotated(
      cloneGridGeoJSON,
      rotationAngle,
      rows,
      columns
    );
    cloneRotationMarker.setLngLat(rotationMarkerPosition as [number, number]);
  };

  const cloneEnvelopeCenterMarker = markerForTrials(
    map,
    cloneEnvelopeCenter as mapboxgl.LngLatLike,
    moveIcon,
    trialPlotId,
    createCustomMarkerElement
  );

  const cloneRotationMarker = markerForTrials(
    map,
    rotationMarkerPosition as [number, number],
    rotateImage,
    trialPlotId,
    createCustomMarkerElement
  );

  markersForTrialPlot[rotationMarkerId] = cloneRotationMarker;
  cloneRotationMarker.on("drag", () => {
    const newCloneCenter = cloneEnvelopeCenterMarker.getLngLat().toArray() as [
      number,
      number
    ];
    const newRotationMarkerPosition = cloneRotationMarker
      .getLngLat()
      .toArray() as [number, number];
    const newAngle = turf.bearing(
      turf.point(newCloneCenter),
      turf.point(newRotationMarkerPosition)
    );
    rotationAngle = newAngle;

    // grid rotations code removed;

    const rotatedGrid = turf.transformRotate(cloneGridGeoJSON, newAngle, {
      pivot: newCloneCenter,
    });
    (map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource).setData(
      rotatedGrid
    );
    updateRotationMarkerPosition(rotatedGrid);

    // Update the replicant group label position when rotating
    updateGroupLabelPosition({
      map,
      baseSourceId: cloneGridSourceId,
      rotatedGrid,
      newTrialState,
      trialPlotId,
      labelSuffix: "'",
      offset: [0, 0],
      fontSize: 12,
    });

    trialItem.replicants[cloneIndex].geojson.geometry.coordinates =
      rotatedGrid.features.map((feature: any) => feature.geometry.coordinates);

    // Update each plot label position for replicant during rotation
    updateReplicantPlotLabels(map, rotatedGrid, cloneIndex, trialPlotId);
  });
  markersForTrialPlot[envelopeCenterMarkerId] = cloneEnvelopeCenterMarker;

  cloneRotationMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    // Report alignment status after user finishes rotating a replicant
    checkAndReportAlignment();
  });
  // Example usage of handleReplicantMove
  cloneEnvelopeCenterMarker.on("drag", () => {
    const newCenter = cloneEnvelopeCenterMarker.getLngLat().toArray() as [
      number,
      number
    ];
    const offset = [
      newCenter[INDEX_ZERO] - cloneEnvelopeCenter[INDEX_ZERO],
      newCenter[INDEX_ONE] - cloneEnvelopeCenter[INDEX_ONE],
    ];

    const newGridFeatures = cloneGridGeoJSON.features.map((feature: any) => {
      const coords = feature.geometry.coordinates[INDEX_ZERO].map(
        (point: [number, number]) => [
          point[INDEX_ZERO] + offset[INDEX_ZERO],
          point[INDEX_ONE] + offset[INDEX_ONE],
        ]
      );
      return turf.polygon([coords], feature.properties);
    });

    cloneGridGeoJSON = turf.featureCollection(newGridFeatures);

    const rotatedGrid = turf.transformRotate(cloneGridGeoJSON, rotationAngle, {
      pivot: newCenter,
    });

    const source = map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(rotatedGrid);
    } else {
      map.addSource(cloneGridSourceId, {
        type: "geojson",
        data: rotatedGrid,
      });
    }

    const rotationMarkerPosition = getNorthWestCorner(
      cloneGridGeoJSON,
      rows,
      columns
    );
    cloneRotationMarker.setLngLat(rotationMarkerPosition as [number, number]);
    updateRotationMarkerPosition(rotatedGrid);

    // Update the replicant group label position
    updateGroupLabelPosition({
      map,
      baseSourceId: cloneGridSourceId,
      rotatedGrid,
      newTrialState,
      trialPlotId,
      labelSuffix: "'",
      offset: [0, 0],
      fontSize: 12,
    });

    gridPositions[replicantId] = {
      lng: newCenter[INDEX_ZERO],
      lat: newCenter[INDEX_ONE],
    };

    // Update each plot label position for replicant
    updateReplicantPlotLabels(map, rotatedGrid, cloneIndex, trialPlotId);

    cloneEnvelopeCenter = newCenter;

    trialItem.replicants[cloneIndex].geojson.geometry.coordinates =
      rotatedGrid.features.map((feature: any) => feature.geometry.coordinates);
  });

  cloneEnvelopeCenterMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    checkAndReportAlignment();
  });

  if (!map.getSource(cloneGridSourceId)) {
    map.addSource(cloneGridSourceId, {
      type: "geojson",
      data: cloneGridGeoJSON,
    });

    map.addLayer({
      id: cloneGridLayerId,
      type: "line",
      source: cloneGridSourceId,
      paint: {
        "line-color": RGBA_TRIAL_PLOT,
        "line-width": 2,
        "line-dasharray": [1, 1], // This makes the line dashed
      },
    });
  } else {
    (map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource).setData(
      cloneGridGeoJSON
    );
  }

  trialItem.replicants[cloneIndex].geojson.geometry.coordinates =
    cloneGridGeoJSON.features.map(
      (feature: any) => feature.geometry.coordinates
    );

  if (replicants.visible) {
    showReplicantLayer(
      cloneGridLayerId,
      map,
      cloneEnvelopeCenterMarker,
      cloneRotationMarker
    );
  } else {
    hideReplicantLayer(
      cloneGridLayerId,
      map,
      cloneEnvelopeCenterMarker,
      cloneRotationMarker
    );
  }

  cloneGridGeoJSON.features.forEach((feature: any, index: any) => {
    // Skip validation for strip plots and their replicants - they should never show red borders
    const isStripPlot = trialItem?.plot?.isStripPlot;
    const isError = isStripPlot
      ? false
      : plotsValidationData?.polygonsNotInsideField?.includes(replicantId) ||
        plotsValidationData.intersectingPairs?.some((item: any) =>
          item.includes(replicantId)
        );
    const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}`;
    if (map.getLayer(cellLayerId)) {
      map.removeLayer(cellLayerId);
    }

    map.addLayer({
      id: cellLayerId,
      type: "fill",
      source: cloneGridSourceId,
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 1,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });

    const cellBorderLayerId = `${cellLayerId}-border`;
    if (map.getLayer(cellBorderLayerId)) {
      map.removeLayer(cellBorderLayerId);
    }

    map.addLayer({
      id: cellBorderLayerId,

      type: "line",
      source: cloneGridSourceId,
      paint: {
        "line-color": isError ? RGBA_TRIAL_PLOT_RED : RGBA_TRANSPARENT_COLOR,
        "line-width": 2,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });
  });

  // Add labels for replicant plots
  cloneGridGeoJSON.features.forEach((feature: any, index: any) => {
    const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}`;
    const labelSourceId = `${cellLayerId}-label`;
    const labelLayerId = `${labelSourceId}-label-plot`;

    // Remove existing label if it exists
    if (map.getLayer(labelLayerId)) {
      map.removeLayer(labelLayerId);
    }
    if (map.getSource(labelSourceId)) {
      map.removeSource(labelSourceId);
    }

    // Add label source
    map.addSource(labelSourceId, {
      type: "geojson",
      data: turf.featureCollection([feature]),
    });

    // Add label layer
    addLabelToMap(
      map,
      labelSourceId,
      `${index + 1}`, // Plot label (1, 2, 3, ...)
      "center",
      [0, 0],
      "plot",
      12
    );
  });

  // Add group label for replicant
  // Find the plotIndex of the parent plot in the trial_plots array
  const parentPlotIndex = newTrialState.trial_plots.findIndex(
    (tp) => tp.plot.id === trialPlotId
  );
  // Use ASCII character for A, B, C... (ensure proper encoding) with prime symbol
  const replicantGroupLabel = String.fromCodePoint(65 + parentPlotIndex) + `'`; // A', B', C'...
  const replicantGroupLabelSourceId = `${cloneGridSourceId}-group-label`;
  const replicantGroupLabelLayerId = `${replicantGroupLabelSourceId}-label-group`;

  // Remove existing group label if it exists
  if (map.getLayer(replicantGroupLabelLayerId)) {
    map.removeLayer(replicantGroupLabelLayerId);
  }
  if (map.getSource(replicantGroupLabelSourceId)) {
    map.removeSource(replicantGroupLabelSourceId);
  }

  // Get the bounds of the grid to position the label outside
  const replicantGridBounds = turf.bbox(cloneGridGeoJSON);
  const replicantGridCenter = turf.center(cloneGridGeoJSON);

  // Create a point slightly above the grid for the label
  const replicantLabelPoint = turf.point([
    replicantGridCenter.geometry.coordinates[0],
    replicantGridBounds[3] +
      (replicantGridBounds[3] - replicantGridBounds[1]) * 0.01, // Very small offset above the grid
  ]);

  map.addSource(replicantGroupLabelSourceId, {
    type: "geojson",
    data: replicantLabelPoint,
  });

  // Add group label layer
  addLabelToMap(
    map,
    replicantGroupLabelSourceId,
    replicantGroupLabel, // Group label (A', B', C', ...)
    "bottom", // Anchor at the bottom of the text so it points to the plot
    [0, -0.2], // Small negative y offset to position closer to the plot
    "group",
    14 // Increased size for better visibility
  );
};

const showReplicantLayer = (
  cloneGridLayerId: string,
  map: mapboxgl.Map,
  cloneEnvelopeCenterMarker: mapboxgl.Marker,
  cloneRotationMarker: mapboxgl.Marker
): void => {
  map.setLayoutProperty(cloneGridLayerId, "visibility", "visible");
  cloneEnvelopeCenterMarker.getElement().style.display = "block";
  cloneRotationMarker.getElement().style.display = "block";
  // gridRotations is no longer populated for plots or replicants (see
  // top-of-file note), so there is no stored rotation to re-apply here.
};

const hideReplicantLayer = (
  cloneGridLayerId: string,
  map: mapboxgl.Map,
  cloneEnvelopeCenterMarker: mapboxgl.Marker,
  cloneRotationMarker: mapboxgl.Marker
): void => {
  map.setLayoutProperty(cloneGridLayerId, "visibility", "none");
  cloneEnvelopeCenterMarker.getElement().style.display = "none";
  cloneRotationMarker.getElement().style.display = "none";
};

/** Fill colour for a rate/dosage cell, chosen by which tab is active. */
interface RatesDosageCellColorParams {
  activeRatesTab: string;
  ratesDosageItem: RatesDosageDataInterface;
  ratesDosageList: RatesDosageDataInterface[];
  fertilizerRate: number;
  index: number;
  variety: any;
  rate: any;
  trialProducts: any;
  unit_of_rate: string;
  bioApplicationIndex: number;
}

function resolveRatesDosageCellColor(params: RatesDosageCellColorParams): any {
  const {
    activeRatesTab,
    ratesDosageItem,
    ratesDosageList,
    fertilizerRate,
    index,
    variety,
    rate,
    trialProducts,
    unit_of_rate,
    bioApplicationIndex,
  } = params;

  if (activeRatesTab === "biological") {
    const bioTreatment = ratesDosageItem.bioTreatement?.[bioApplicationIndex];
    const selectedTreatment = bioTreatment
      ? bioTreatment.selected_treatment[0]
      : null;
    return getCircleColorBiologicals(bioTreatment, selectedTreatment);
  }
  if (activeRatesTab === "nutrient") {
    return (
      ratesDosageItem.mapFillColor ??
      getCircleColorNutrient(ratesDosageList, fertilizerRate, undefined, index)
    );
  }
  return getCircleColor(
    variety,
    rate,
    trialProducts,
    unit_of_rate,
    ratesDosageList
  );
}

interface RatesDosageCellContext {
  map: mapboxgl.Map;
  layer: any;
  index: number;
  itemIndex: number;
  ratesDosageItem: RatesDosageDataInterface;
  ratesDosageList: RatesDosageDataInterface[];
  activeRatesTab: string;
  trialProducts: any;
  unit_of_rate: string;
  bioApplicationIndex: number;
}

/** Colours a plot's fill layer and wires its hover cursor/event dispatch. */
function styleFillLayer(ctx: RatesDosageCellContext): void {
  const { map, layer, index, itemIndex, ratesDosageItem } = ctx;
  const color = resolveRatesDosageCellColor({
    activeRatesTab: ctx.activeRatesTab,
    ratesDosageItem,
    ratesDosageList: ctx.ratesDosageList,
    fertilizerRate: ratesDosageItem.fertiliser_rate ?? 0,
    index,
    variety: ratesDosageItem.variety,
    rate: ratesDosageItem.rate,
    trialProducts: ctx.trialProducts,
    unit_of_rate: ctx.unit_of_rate,
    bioApplicationIndex: ctx.bioApplicationIndex,
  });

  if (color) {
    map.setPaintProperty(layer.id, "fill-color", color);
  }

  map.on("mouseenter", layer.id, () => {
    window.dispatchEvent(
      new CustomEvent("subplotMouseOver", {
        detail: {
          message: `focus on from plot ${index} ${itemIndex}`,
          index,
          subIndex: itemIndex,
          type: "enter",
        },
      })
    );
    map.getCanvas().style.cursor = "pointer";
  });

  map.on("mouseleave", layer.id, () => {
    window.dispatchEvent(
      new CustomEvent("subplotMouseOver", {
        detail: {
          message: `focus removed from plot ${index} ${itemIndex}`,
          type: "leave",
          index,
          subIndex: itemIndex,
        },
      })
    );
    map.getCanvas().style.cursor = "";
  });
}

/** Colours/widths a plot's border (line) layer, highlighting collapsed cells. */
function styleLineLayer(
  map: mapboxgl.Map,
  layer: any,
  plotId: string,
  filterLastIdPart: any,
  ratesDosageItem: RatesDosageDataInterface
): void {
  const collapseValueArray = ratesDosageItem.collapseValue || [];
  const isCollapseIdPresent = collapseValueArray.includes(filterLastIdPart);

  if (plotId === filterLastIdPart) {
    map.setPaintProperty(layer.id, "line-color", RGBA_TRIAL_PLOT);
  }
  map.setPaintProperty(
    layer.id,
    "line-width",
    isCollapseIdPresent ? ratesDosageItem.mapOutlineWidth ?? 4 : 1
  );
}

const addRatesAndDosageToMap = (
  ratesDosagesArray: RatesDosageDataInterface[][],
  map: mapboxgl.Map | null,
  trialProducts: any,
  unit_of_rate: string,
  isFertilizerTabActive: boolean,
  activeRatesTab: string,
  bioApplicationIndex: number
): void => {
  if (!Array.isArray(ratesDosagesArray) || !map) return;

  ratesDosagesArray.forEach((ratesDosageList, index: number) => {
    ratesDosageList.forEach((ratesDosageItem, itemIndex) => {
      const borderBoxId = `${
        ratesDosageItem.plot_id as number
      }-${index}-border`;
      const plotId = borderBoxId.replace(/-\d+-border$/, "");

      map.getStyle()?.layers?.forEach((layer: any) => {
        const filterIdParts = layer?.filter;
        if (!filterIdParts || filterIdParts.length === 0) return;

        const filterLastIdPart = filterIdParts[filterIdParts.length - 1];
        if (plotId !== filterLastIdPart) return;

        // replicant id === plotid, it will have a layer with type fill
        if (layer.type === "fill") {
          styleFillLayer({
            map,
            layer,
            index,
            itemIndex,
            ratesDosageItem,
            ratesDosageList,
            activeRatesTab,
            trialProducts,
            unit_of_rate,
            bioApplicationIndex,
          });
        }

        if (layer.type === "line") {
          styleLineLayer(map, layer, plotId, filterLastIdPart, ratesDosageItem);
        }
      });
    });
  });
};
